'use client'

export const dynamic = 'force-dynamic'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import {
  BadgeCheck, Bell, Heart, HandHeart, MessageCircle, Share2, Megaphone, CalendarDays,
  HeartHandshake, Landmark, Send, UserRound, TrendingUp, MapPin, LogIn,
  AlertTriangle, Trash2, Pencil, ShieldAlert,
  MessageSquare, HelpCircle, ThumbsUp, Search, LifeBuoy, Trophy, Droplet, Car, Tag, BedDouble,
  Volume2, Play, Siren, Phone,
  type LucideIcon,
} from 'lucide-react'
import FeedGallery from './FeedGallery'
import { KitCard, EmptyState, PageHero } from '../../components/ui/kit'
import { useSocial } from '../../hooks/useSocial'
import AdSlot from '../../components/ui/AdSlot'

/* ─────────────────────────────────────────────────────────────
   COMMUNITY FEED (Phase 3 — community-only) — REAL user posts only
   (ask/dua/recommend/lost_found/help). Masjid notices/events live on
   /announcements; campaigns on /give; listings on /directory. Open
   compose for any signed-in member; reactions/comments via
   /api/feed/interactions. No invented actors, no fake counts.
───────────────────────────────────────────────────────────── */

interface FeedItem {
  id: string
  kind: 'announcement' | 'campaign' | 'listing' | 'post'
  type: string
  title: string
  body: string
  masjid?: string
  masjidSlug?: string
  city?: string
  time: string
  photo?: string | null
  photos?: string[]        // up to 5 photos (gallery); photo = photos[0] for back-compat
  href: string
  priority: boolean
  // user-post fields (kind==='post'). item.id is already the raw post uuid for interactions.
  actor?: 'masjid' | 'user'
  authorName?: string
  authorUserId?: string
  authorAvatar?: string   // author's profile photo (users.avatar_url); falls back to initials when absent
  authorTitle?: string    // PUBLIC role label ('Admin' | 'Moderator'), pre-mapped server-side; absent otherwise
  createdAt?: string | null // raw ISO for relative-time rendering; `time` is the preformatted fallback
  category?: string       // general | ask | dua | recommend | lost_found | help | blood
  urgent?: boolean
  postId?: string         // redundant mirror of item.id; use item.id for interactions
  distanceKm?: number     // Phase 4 (Nearby tab ONLY): km from the viewer; absent otherwise
  // Structured blood detail (category==='blood'). DISPLAY only — never carries attendantPhone.
  blood?: BloodDetail
  // Structured carpool detail (category==='carpool'). No money; contact is plain optional.
  carpool?: CarpoolDetail
  // Structured event detail (category==='event'). No money; rsvpContact is plain optional.
  event?: EventDetail
  // Structured item-for-sale detail (category==='sale'). pricePaise raw|null → "Price not listed".
  item?: ItemDetail
  // Structured accommodation detail (category==='accommodation'). rentPaise raw|null → "Rent not listed".
  accommodation?: AccommodationDetail
  // Structured emergency detail (category==='emergency'). contact is PUBLIC (shown directly — no reveal).
  emergency?: EmergencyDetail
}

type EmergencyDetail = {
  incidentType: string
  area: string | null
  city: string | null
  severity: string | null
  description: string | null
  contact: string
  expiresAt: string | null
  resolved: boolean
}

type BloodDetail = {
  bloodGroup: string
  hospital: string
  city: string | null
  unitsNeeded: number | null
  urgency: string | null
  patientContext: string | null
  expiresAt: string | null
  resolved: boolean
}

type CarpoolDetail = {
  direction: string
  fromLocation: string
  toLocation: string
  travelAt: string | null
  seats: number | null
  contact: string | null
}

type EventDetail = {
  title: string
  eventAt: string
  venue: string
  rsvpContact: string | null
}

type ItemDetail = {
  title: string
  pricePaise: number | null
  condition: string | null
  pickupLocation: string | null
  contact: string | null
}

type AccommodationDetail = {
  direction: string
  area: string
  rentPaise: number | null
  rooms: string | null
  availableFrom: string | null
  contact: string | null
}

type UrgentItem = { id: string; body: string; author: string; createdAt: string | null }

// Category → display label for the badge / picker. 'general' shows no badge.
const CATEGORY_LABEL: Record<string, string> = {
  ask: 'Ask', dua: 'Dua', recommend: 'Recommend', lost_found: 'Lost & found', help: 'Help', blood: 'Blood request', achievement: 'Achievement', emergency: 'Emergency',
}
// Category → compose-chip icon (net-new). 'general' is intentionally absent from CATEGORY_LABEL
// (no card badge) but HAS an icon for its compose chip. Future Phase-2 types (carpool/sale/
// accommodation/event) are pre-mapped so their chips need no icon work when they land.
const CATEGORY_ICON: Record<string, LucideIcon> = {
  general: MessageSquare, ask: HelpCircle, dua: HandHeart, recommend: ThumbsUp,
  lost_found: Search, help: LifeBuoy, blood: Droplet, achievement: Trophy,
  carpool: Car, sale: Tag, accommodation: BedDouble, event: CalendarDays, emergency: Siren,
}
// Blood-request compose whitelist (mirrors the server BLOOD_GROUPS set).
// Focus/visibility auto-refresh cooldown — a page-0 refresh within this window is skipped, so tab
// switches / window refocus don't refetch on every tiny transition. NOT polling (no interval).
const FOCUS_REFRESH_COOLDOWN_MS = 30_000
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'] as const
const BLOOD_EXPIRY_OPTIONS = [1, 2, 3, 7] as const
// Carpool display helpers.
const carpoolDirectionLabel = (d: string) => (d === 'seeking' ? 'Looking for a ride' : 'Offering a ride')
function carpoolTravelLabel(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}
// Event date-time display (ISO → localized, with year since events are dated).
function eventDateLabel(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
// 🔴 Money DISPLAY ONLY — paise → ₹ with Indian grouping. NEVER used for any sum/average/aggregate.
// Called ONLY for a non-null price; a null price renders the literal "Price not listed" instead (never ₹0).
function formatMoney(paise: number): string {
  return `₹${(paise / 100).toLocaleString('en-IN')}`
}
/* Phase 4 Slice 1 — best-effort location capture (masjid-form navigator.geolocation pattern). Resolves
   to coords or NULL and NEVER throws / NEVER hangs past the timeout, so a GPS denial, error, or slow
   fix can never block a post. Callers attach the coords only when a structured type is being posted. */
function captureCoords(timeoutMs = 5000): Promise<{ latitude: number; longitude: number } | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null)
  return new Promise((resolve) => {
    let settled = false
    const finish = (v: { latitude: number; longitude: number } | null) => { if (!settled) { settled = true; resolve(v) } }
    const timer = setTimeout(() => finish(null), timeoutMs)
    navigator.geolocation.getCurrentPosition(
      (pos) => { clearTimeout(timer); finish({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }) },
      () => { clearTimeout(timer); finish(null) },   // denied / unavailable → null, post still proceeds
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    )
  })
}
// Human label for the optional item condition.
const CONDITION_LABEL: Record<string, string> = { new: 'New', like_new: 'Like new', used: 'Used' }
// Accommodation helpers: direction label + available-from date ('YYYY-MM-DD' → readable).
const accDirectionLabel = (d: string) => (d === 'seeking' ? 'Looking for a place' : 'Offering a place')
function accAvailLabel(d: string | null): string {
  if (!d) return ''
  const dt = new Date(d + 'T00:00:00')
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}
// "expires in Nd" from an ISO timestamp (read path already excludes truly-expired posts).
function bloodExpiryLabel(iso: string | null): string {
  if (!iso) return ''
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return 'expiring now'
  const days = Math.ceil(ms / 86_400_000)
  return `expires in ${days}d`
}
// Emergency alerts expire in HOURS (a much shorter, incident-scale horizon than blood's days).
function emergencyExpiryLabel(iso: string | null): string {
  if (!iso) return ''
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return 'expiring now'
  const hours = Math.ceil(ms / 3_600_000)
  if (hours < 24) return `expires in ${hours}h`
  const days = Math.ceil(hours / 24)
  return `expires in ${days}d`
}
// Emergency compose whitelists (mirror the server EMERGENCY_* sets).
const EMERGENCY_INCIDENT_TYPES = ['Medical', 'Fire', 'Accident', 'Missing person', 'Security', 'Other'] as const
const EMERGENCY_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const
const EMERGENCY_EXPIRY_OPTIONS = [6, 12, 24, 48] as const
// The compose picker — General (default) first, then Ask/Dua/Recommend, the rest available.
const COMPOSE_CATEGORIES: Array<{ id: string; label: string }> = [
  { id: 'general', label: 'General' },
  { id: 'ask', label: 'Ask' },
  { id: 'dua', label: 'Dua' },
  { id: 'recommend', label: 'Recommend' },
  { id: 'lost_found', label: 'Lost & found' },
  { id: 'help', label: 'Help' },
]
// The client-side filter row (All + each user-post category). 'general' is EXCLUDED here — it's a
// compose-only default (no badge), and 'All' already covers plain posts, so a General filter chip
// would be redundant.
const FILTER_CATEGORIES: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'All' }, ...COMPOSE_CATEGORIES.filter((c) => c.id !== 'general'),
]


const TYPE_ICON: Record<string, any> = {
  Janaza: Bell, Notice: Megaphone, Event: CalendarDays, Program: CalendarDays,
  Campaign: HeartHandshake, 'New listing': Landmark, Khutba: Megaphone,
  Finance: HeartHandshake, Update: Megaphone,
}

type FeedComment = { id: string; body: string; author: string; createdAt: string | null }

function timeAgo(value: string | null | undefined): string {
  if (!value) return ''
  const then = new Date(value).getTime()
  if (Number.isNaN(then)) return ''
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`
  return new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

// Public role chip. `title` is already the mapped public label ('Admin' | 'Moderator') — the raw RBAC
// role never reaches here. Renders nothing for any other value (defensive; callers already gate).
function RoleBadge({ title }: { title: string }) {
  if (title !== 'Admin' && title !== 'Moderator') return null
  return <span className={'fp-role ' + (title === 'Admin' ? 'fp-role-admin' : 'fp-role-mod')}>{title}</span>
}

/* SS4c Piece C1 — an adhan-link post: its body carries a per-adhan landing-page URL that the mobile
   "Share to feed" action posts. Match the app's OWN adhan URL shape (masjids.in / www.masjids.in /
   masjids-in.vercel.app + /adhan/{uuid}) so we never false-positive on other links, and extract the
   id. The card just LINKS to /adhan/{id} — we never store or play audio here, so the landing page's
   every-render approval check (Piece A) is inherited: a later-unapproved adhan 404s at the link. */
const ADHAN_LINK_RE = /https?:\/\/(?:www\.)?(?:masjids\.in|masjids-in\.vercel\.app)\/adhan\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i
function extractAdhanId(body: string | null | undefined): string | null {
  if (!body) return null
  const m = ADHAN_LINK_RE.exec(body)
  return m ? m[1] : null
}

function PostCard({ item, followed, onFollow, user, bloodEnabled = false, carpoolEnabled = false, eventEnabled = false, saleEnabled = false, accommodationEnabled = false, emergencyEnabled = false, onResolved, onDelete, onEdit, onModerate }: {
  item: FeedItem
  followed: boolean
  onFollow: () => void
  user: any
  bloodEnabled?: boolean
  carpoolEnabled?: boolean
  eventEnabled?: boolean
  saleEnabled?: boolean
  accommodationEnabled?: boolean
  emergencyEnabled?: boolean
  onResolved?: () => void
  onDelete?: (id: string) => void
  onEdit?: (id: string, bodyText: string) => Promise<boolean>
  onModerate?: (id: string, action: 'remove' | 'delete') => void
}) {
  const [resolving, setResolving] = useState(false)
  const [revealing, setRevealing] = useState(false)
  const [revealedPhone, setRevealedPhone] = useState<string | null>(null)
  const [revealError, setRevealError] = useState<string | null>(null)
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [reacted, setReacted] = useState(false)
  const [comments, setComments] = useState<FeedComment[]>([])
  const [threadOpen, setThreadOpen] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [posting, setPosting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [shared, setShared] = useState(false)
  // Author inline text-edit.
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(item.body)
  const [savingEdit, setSavingEdit] = useState(false)
  const Icon = TYPE_ICON[item.type] || Megaphone

  useEffect(() => {
    let alive = true
    fetch(`/api/feed/interactions?itemId=${encodeURIComponent(item.id)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d) return
        if (d.summary) setCounts(d.summary)
        setReacted(Boolean(d.viewerReacted))
        if (Array.isArray(d.comments)) setComments(d.comments)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [item.id])

  const toggleReact = async () => {
    if (!user) { setThreadOpen(true); return } // action-gate guests → sign-in CTA in the thread
    if (busy) return
    setBusy(true)
    const optimistic = !reacted
    setReacted(optimistic)
    setCounts((c) => ({ ...c, react: Math.max(0, (c.react || 0) + (optimistic ? 1 : -1)) }))
    try {
      const res = await fetch('/api/feed/interactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId: item.id, itemType: item.kind, kind: 'reaction' }),
      })
      const d = await res.json()
      if (res.ok) {
        if (typeof d.viewerReacted === 'boolean') setReacted(d.viewerReacted)
        if (d.summary) setCounts(d.summary)
      } else {
        setReacted(!optimistic) // revert on failure
        setCounts((c) => ({ ...c, react: Math.max(0, (c.react || 0) + (optimistic ? -1 : 1)) }))
      }
    } catch {
      setReacted(!optimistic)
    } finally {
      setBusy(false)
    }
  }

  const postComment = async () => {
    const text = commentText.trim()
    if (!text || posting) return
    setPosting(true)
    try {
      const res = await fetch('/api/feed/interactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId: item.id, itemType: item.kind, kind: 'comment', body: text }),
      })
      const d = await res.json()
      if (res.ok && d.comment) {
        setComments((cs) => [...cs, d.comment])
        if (d.summary) setCounts(d.summary)
        setCommentText('')
      }
    } catch {} finally {
      setPosting(false)
    }
  }

  const share = async () => {
    const url = `${window.location.origin}${item.href}`
    try {
      if (navigator.share) await navigator.share({ title: item.title || item.body.slice(0, 60), url })
      else { await navigator.clipboard.writeText(url); setShared(true); setTimeout(() => setShared(false), 1800) }
    } catch {}
  }

  const saveEdit = async () => {
    const text = editText.trim()
    if (!text || savingEdit) return
    setSavingEdit(true)
    const ok = await onEdit?.(item.id, text)
    setSavingEdit(false)
    if (ok) setEditing(false)   // parent already applied the new body optimistically
  }

  const markResolved = async () => {
    if (resolving) return
    setResolving(true)
    try {
      const res = await fetch(`/api/community-feed/${item.id}/resolve`, { method: 'PATCH', credentials: 'same-origin' })
      if (res.ok) onResolved?.()   // parent reloads → the now-resolved post drops from the feed
    } catch {} finally { setResolving(false) }
  }

  // Tap-to-reveal: the ONLY path that fetches the attendant number. It's never in the feed
  // payload — it appears only after this successful, logged, rate-limited reveal tap.
  const revealContact = async () => {
    if (revealing || revealedPhone) return
    setRevealing(true)
    setRevealError(null)
    try {
      const res = await fetch(`/api/community-feed/${item.id}/reveal`, { method: 'POST', credentials: 'same-origin' })
      if (res.status === 401) { window.location.href = '/login'; return }
      const d = await res.json().catch(() => ({}))
      if (res.ok && d.phone) {
        setRevealedPhone(String(d.phone))
      } else if (res.status === 429) {
        setRevealError(d?.error || "You've viewed several contacts today — try again later.")
      } else if (res.status === 410 || res.status === 409) {
        setRevealError('This request is no longer active.')
        onResolved?.()   // refresh → the stale post drops from the feed
      } else {
        setRevealError(d?.error || 'Could not show the contact right now.')
      }
    } catch {
      setRevealError('Could not show the contact right now.')
    } finally {
      setRevealing(false)
    }
  }

  // HONESTY GUARD: a user post must NEVER show the verified badge, a masjid link, the
  // "Masjids.in" fallback, or Follow. Branch on actor before rendering anything.
  const isUser = item.actor === 'user'
  const isDua = item.category === 'dua'
  // Blood card renders only when the flag is on AND structured data is present (the read path
  // already excluded expired/resolved blood). Author-only gate for "Mark as resolved".
  const isBlood = isUser && item.category === 'blood' && bloodEnabled && !!item.blood
  // Emergency card — highlighted, flag-gated, structured. Contact is shown DIRECTLY (no reveal gate);
  // the read path already excluded resolved/expired emergencies. Author-only "Mark as resolved".
  const isEmergency = isUser && item.category === 'emergency' && emergencyEnabled && !!item.emergency
  const isCarpool = isUser && item.category === 'carpool' && carpoolEnabled && !!item.carpool
  const isEvent = isUser && item.category === 'event' && eventEnabled && !!item.event
  const isSale = isUser && item.category === 'sale' && saleEnabled && !!item.item
  const isAccommodation = isUser && item.category === 'accommodation' && accommodationEnabled && !!item.accommodation
  // Adhan-link post (any user post whose body carries a real /adhan/{id} URL). Independent of
  // category (posted as 'general'); a link-card that opens the gated landing page.
  const adhanId = isUser ? extractAdhanId(item.body) : null
  const isAdhan = !!adhanId
  const adhanCaption = isAdhan ? (item.body || '').replace(ADHAN_LINK_RE, '').trim() : ''
  const isAuthor = !!(user && item.authorUserId && String(user.id) === String(item.authorUserId))
  // Platform moderator (moderator-inclusive set — matches who /api/admin/feed/reported accepts).
  const isAdmin = !!user && ['super_admin', 'state_admin', 'moderator'].includes(String(user.role))
  const categoryBadge = isUser && item.category && CATEGORY_LABEL[item.category] ? CATEGORY_LABEL[item.category] : null
  const reactLabel = isDua ? 'Ameen' : 'Like'

  return (
    <article className={'fp-card' + (item.priority ? ' priority' : '') + (isUser ? ' user' : '')}>
      {item.priority && <div className="fp-priority"><Bell size={11} strokeWidth={1.5} /> TIME-CRITICAL NOTICE</div>}
      <header className="fp-head">
        {isUser ? (
          <>
            {item.authorUserId ? (
              <Link href={`/u/${item.authorUserId}`} className="fp-avatar fp-avatar-user" aria-hidden>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                {item.authorAvatar ? <img src={item.authorAvatar} alt="" className="fp-avatar-img" /> : (item.authorName || 'M').charAt(0).toUpperCase()}
              </Link>
            ) : (
              <span className="fp-avatar fp-avatar-user" aria-hidden>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                {item.authorAvatar ? <img src={item.authorAvatar} alt="" className="fp-avatar-img" /> : (item.authorName || 'M').charAt(0).toUpperCase()}
              </span>
            )}
            <div className="fp-id">
              <span className="fp-name-row">
                {item.authorUserId ? (
                  <Link href={`/u/${item.authorUserId}`} className="fp-name fp-name-user">{item.authorName || 'Member'}</Link>
                ) : (
                  <span className="fp-name fp-name-user">{item.authorName || 'Member'}</span>
                )}
                {item.authorTitle && <RoleBadge title={item.authorTitle} />}
              </span>
              <div className="fp-meta">
                {categoryBadge && <span className={'fp-cat fp-cat-' + item.category}>{categoryBadge}</span>}
                {item.city && <span>{item.city}</span>}
                <span>{timeAgo(item.createdAt) || item.time}</span>
                {/* Phase 4: distance from the viewer — present ONLY on the nearby tab. */}
                {item.distanceKm != null && <span className="fp-dist">📍 {item.distanceKm} km away</span>}
              </div>
            </div>
          </>
        ) : (
          <>
            <Link href={item.masjidSlug ? `/masjids/${item.masjidSlug}` : item.href} className="fp-avatar">
              {(item.masjid || item.type).charAt(0)}
            </Link>
            <div className="fp-id">
              <Link href={item.masjidSlug ? `/masjids/${item.masjidSlug}` : item.href} className="fp-name">
                {item.masjid || 'Masjids.in'} <BadgeCheck size={13} strokeWidth={1.5} />
              </Link>
              <div className="fp-meta">
                <span className="fp-type"><Icon size={10} strokeWidth={1.5} /> {item.type.toUpperCase()}</span>
                {item.city && <span>{item.city}</span>}
                <span>{timeAgo(item.createdAt) || item.time}</span>
              </div>
            </div>
            {item.masjidSlug && (
              <button type="button" className={'fp-follow' + (followed ? ' on' : '')} onClick={onFollow}>
                {followed ? 'Following' : 'Follow'}
              </button>
            )}
          </>
        )}
      </header>

      {isUser ? (
        isEmergency ? (
          <div className="fp-body fp-body-user fp-emergency">
            <div className="fp-emergency-strip"><Siren size={13} strokeWidth={2} /> EMERGENCY{item.emergency!.severity ? ` · ${item.emergency!.severity.toUpperCase()}` : ''}</div>
            <div className="fp-emergency-facts">
              <span className="fp-emergency-type">{item.emergency!.incidentType}</span>
              {(item.emergency!.area || item.emergency!.city) && (
                <span className="fp-emergency-chip"><MapPin size={11} strokeWidth={2} /> {[item.emergency!.area, item.emergency!.city].filter(Boolean).join(', ')}</span>
              )}
              <span className="fp-emergency-chip">{emergencyExpiryLabel(item.emergency!.expiresAt)}</span>
            </div>
            {item.emergency!.description && <p className="fp-emergency-detail">{item.emergency!.description}</p>}
            {item.body && <p className="fp-emergency-detail">{item.body}</p>}
            {/* Contact is PUBLIC on an emergency (fastest path to the poster) — shown directly, no reveal. */}
            {item.emergency!.contact && (
              <a href={`tel:${item.emergency!.contact}`} className="fp-emergency-call">
                <Phone size={14} strokeWidth={2} /> Call {item.emergency!.contact}
              </a>
            )}
            {isAuthor && !item.emergency!.resolved && (
              <button type="button" className="fp-blood-resolve" onClick={markResolved} disabled={resolving}>
                {resolving ? 'Resolving…' : 'Mark as resolved'}
              </button>
            )}
          </div>
        ) : isBlood ? (
          <div className="fp-body fp-body-user fp-blood">
            <div className="fp-blood-facts">
              <span className="fp-blood-group">{item.blood!.bloodGroup}</span>
              {item.blood!.unitsNeeded != null && <span className="fp-blood-chip">{item.blood!.unitsNeeded} unit{item.blood!.unitsNeeded === 1 ? '' : 's'}</span>}
              {item.blood!.urgency && <span className="fp-blood-chip urgent">{item.blood!.urgency}</span>}
              <span className="fp-blood-chip">{bloodExpiryLabel(item.blood!.expiresAt)}</span>
            </div>
            <p className="fp-blood-hospital">{item.blood!.hospital}{item.blood!.city ? ` · ${item.blood!.city}` : ''}</p>
            {item.body && <p className="fp-blood-detail">{item.body}</p>}
            {/* Phase 4: tap-to-reveal. The number is fetched ONLY on tap (logged + rate-limited
                server-side) and shown in-place; it is never part of the feed payload. */}
            {revealedPhone ? (
              <a href={`tel:${revealedPhone}`} className="fp-blood-donate fp-blood-phone">
                Call {revealedPhone} · {item.blood!.hospital}
              </a>
            ) : (
              <button type="button" className="fp-blood-donate" onClick={revealContact} disabled={revealing}>
                {revealing ? 'Showing contact…' : 'I can donate — show contact'}
              </button>
            )}
            {revealError && <small className="fp-blood-error">{revealError}</small>}
            <small className="fp-blood-sub">Contact revealed only when you tap · each reveal is logged</small>
            {isAuthor && !item.blood!.resolved && (
              <button type="button" className="fp-blood-resolve" onClick={markResolved} disabled={resolving}>
                {resolving ? 'Resolving…' : 'Mark as resolved'}
              </button>
            )}
          </div>
        ) : isCarpool ? (
          <div className="fp-body fp-body-user fp-carpool">
            <div className="fp-carpool-dir"><Car size={14} strokeWidth={2} /> {carpoolDirectionLabel(item.carpool!.direction)}</div>
            <p className="fp-carpool-route"><span>{item.carpool!.fromLocation}</span> <span className="fp-carpool-arrow">→</span> <span>{item.carpool!.toLocation}</span></p>
            <div className="fp-carpool-meta">
              {item.carpool!.travelAt && <span className="fp-carpool-chip">{carpoolTravelLabel(item.carpool!.travelAt)}</span>}
              {item.carpool!.seats != null && <span className="fp-carpool-chip">{item.carpool!.seats} seat{item.carpool!.seats === 1 ? '' : 's'}</span>}
            </div>
            {item.body && <p className="fp-carpool-notes">{item.body}</p>}
            {item.carpool!.contact && (
              <a href={`tel:${item.carpool!.contact}`} className="fp-carpool-contact">Contact: {item.carpool!.contact}</a>
            )}
          </div>
        ) : isEvent ? (
          <div className="fp-body fp-body-user fp-event">
            <p className="fp-event-title">{item.event!.title}</p>
            <div className="fp-event-meta">
              <span className="fp-event-when"><CalendarDays size={13} strokeWidth={2} /> {eventDateLabel(item.event!.eventAt)}</span>
              <span className="fp-event-venue"><MapPin size={13} strokeWidth={2} /> {item.event!.venue}</span>
            </div>
            {item.body && <p className="fp-event-notes">{item.body}</p>}
            {item.event!.rsvpContact && (
              <a href={`tel:${item.event!.rsvpContact}`} className="fp-event-rsvp">RSVP: {item.event!.rsvpContact}</a>
            )}
          </div>
        ) : isSale ? (
          <div className="fp-body fp-body-user fp-sale">
            <div className="fp-sale-head">
              <p className="fp-sale-title"><Tag size={13} strokeWidth={2} /> {item.item!.title}</p>
              {/* 🔴 HONEST-EMPTY: null price → "Price not listed" (never ₹0 / never fabricated). */}
              {item.item!.pricePaise != null
                ? <span className="fp-sale-price">{formatMoney(item.item!.pricePaise)}</span>
                : <span className="fp-sale-price fp-sale-noprice">Price not listed</span>}
            </div>
            {(item.item!.condition || item.item!.pickupLocation) && (
              <div className="fp-sale-meta">
                {item.item!.condition && <span className="fp-sale-chip">{CONDITION_LABEL[item.item!.condition] || item.item!.condition}</span>}
                {item.item!.pickupLocation && <span className="fp-sale-loc"><MapPin size={12} strokeWidth={2} /> {item.item!.pickupLocation}</span>}
              </div>
            )}
            {item.body && <p className="fp-sale-notes">{item.body}</p>}
            {item.item!.contact && (
              <a href={`tel:${item.item!.contact}`} className="fp-sale-contact">Contact: {item.item!.contact}</a>
            )}
          </div>
        ) : isAccommodation ? (
          <div className="fp-body fp-body-user fp-sale">
            <div className="fp-sale-head">
              <p className="fp-sale-title"><BedDouble size={13} strokeWidth={2} /> {accDirectionLabel(item.accommodation!.direction)}</p>
              {/* 🔴 HONEST-EMPTY: null rent → "Rent not listed" (never ₹0 / never fabricated) — same as Sale. */}
              {item.accommodation!.rentPaise != null
                ? <span className="fp-sale-price">{formatMoney(item.accommodation!.rentPaise)}</span>
                : <span className="fp-sale-price fp-sale-noprice">Rent not listed</span>}
            </div>
            <p className="fp-sale-notes" style={{ fontWeight: 700, color: 'var(--f-navy)', margin: '0 0 6px' }}><MapPin size={12} strokeWidth={2} /> {item.accommodation!.area}</p>
            {(item.accommodation!.rooms || item.accommodation!.availableFrom) && (
              <div className="fp-sale-meta">
                {item.accommodation!.rooms && <span className="fp-sale-chip">{item.accommodation!.rooms}</span>}
                {item.accommodation!.availableFrom && <span className="fp-sale-chip">Available from {accAvailLabel(item.accommodation!.availableFrom)}</span>}
              </div>
            )}
            {item.body && <p className="fp-sale-notes">{item.body}</p>}
            {item.accommodation!.contact && (
              <a href={`tel:${item.accommodation!.contact}`} className="fp-sale-contact">Contact: {item.accommodation!.contact}</a>
            )}
          </div>
        ) : editing ? (
          <div className="fp-body fp-body-user fp-edit">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              maxLength={2000}
              rows={3}
              autoFocus
              aria-label="Edit your post"
            />
            <div className="fp-edit-actions">
              <button type="button" className="fp-edit-save" disabled={savingEdit || !editText.trim()} onClick={saveEdit}>
                {savingEdit ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="fp-edit-cancel" disabled={savingEdit} onClick={() => { setEditing(false); setEditText(item.body) }}>
                Cancel
              </button>
            </div>
          </div>
        ) : isAdhan ? (
          <div className="fp-body fp-body-user fp-adhan">
            {/* Whole card is a link to the GATED landing page (/adhan/{id}). No inline audio, no
                stored URL — playback + approval re-check happen on Piece A's page. */}
            <Link href={`/adhan/${adhanId}`} className="fp-adhan-card">
              <span className="fp-adhan-icon"><Volume2 size={17} strokeWidth={2} /></span>
              <span className="fp-adhan-main">
                <span className="fp-adhan-label">Adhan · Masjids.in</span>
                {adhanCaption && <span className="fp-adhan-caption">{adhanCaption}</span>}
              </span>
              <span className="fp-adhan-play"><Play size={13} strokeWidth={2} /> Listen</span>
            </Link>
          </div>
        ) : (
          <div className="fp-body fp-body-user">
            <p>{item.body}</p>
          </div>
        )
      ) : (
        <Link href={item.href} className="fp-body">
          {item.title ? <h3>{item.title}</h3> : null}
          <p>{item.body}</p>
        </Link>
      )}
      {/* 2–5 photos → swipeable gallery + dots + full-screen viewer; 1 photo → as today. */}
      {Array.isArray(item.photos) && item.photos.length > 1 ? (
        <FeedGallery photos={item.photos} />
      ) : (item.photo || (item.photos && item.photos[0])) ? (
        <Link href={item.href} className="fp-photo">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={item.photo || item.photos![0]} alt="" loading="lazy" />
        </Link>
      ) : null}

      <footer className="fp-actions">
        <button type="button" className={(reacted ? 'on' : '') + (isDua ? ' ameen' : '')} onClick={toggleReact} disabled={busy} aria-pressed={reacted} aria-label={reactLabel}>
          {isDua
            ? <HandHeart size={14} strokeWidth={1.5} fill={reacted ? 'currentColor' : 'none'} />
            : <Heart size={14} strokeWidth={1.5} fill={reacted ? 'currentColor' : 'none'} />}
          {' '}{counts.react ? `${reactLabel} · ${counts.react}` : reactLabel}
        </button>
        <button type="button" className={threadOpen ? 'on-c' : ''} onClick={() => setThreadOpen((o) => !o)} aria-expanded={threadOpen}>
          <MessageCircle size={14} strokeWidth={1.5} /> {counts.comment || ''}
        </button>
        <button type="button" onClick={share}>
          <Share2 size={14} strokeWidth={1.5} /> {shared ? 'Copied!' : ''}
        </button>
        <span className="fp-spacer" />
        {isAuthor && !editing && !isBlood && !isEmergency && (
          <button
            type="button"
            className="fp-edit-btn"
            aria-label="Edit this post"
            onClick={() => { setEditText(item.body); setEditing(true) }}
          >
            <Pencil size={14} strokeWidth={1.5} /> Edit
          </button>
        )}
        {isAuthor && (
          <button
            type="button"
            className="fp-delete"
            aria-label="Delete this post"
            onClick={() => {
              if (window.confirm("Delete this post? This can't be undone by you.")) onDelete?.(item.id)
            }}
          >
            <Trash2 size={14} strokeWidth={1.5} /> Delete
          </button>
        )}
        {/* Moderator controls on SOMEONE ELSE'S post — visually distinct from the author trash
            (amber shield, "Remove"). An admin who authored the post uses their own Delete above. */}
        {isAdmin && !isAuthor && (
          <>
            <button
              type="button"
              className="fp-moderate"
              aria-label="Remove this post (moderator)"
              onClick={() => {
                if (window.confirm('Remove this post from the feed? (moderator action — reversible from /admin/feed)')) onModerate?.(item.id, 'remove')
              }}
            >
              <ShieldAlert size={14} strokeWidth={1.5} /> Remove
            </button>
            <button
              type="button"
              className="fp-moderate-del"
              aria-label="Delete this post permanently (moderator)"
              onClick={() => {
                if (window.confirm('DELETE this post decisively? Permanent moderator takedown — use "Remove" instead for a reversible hide.')) onModerate?.(item.id, 'delete')
              }}
            >
              Delete
            </button>
          </>
        )}
        {!isUser && <Link href={item.href} className="fp-open">Open →</Link>}
      </footer>

      {threadOpen && (
        <div className="fp-thread">
          {comments.length > 0 ? (
            <ul className="fp-comments">
              {comments.map((c) => (
                <li key={c.id} className="fp-comment">
                  <span className="fp-comment-avatar">{(c.author || 'M').charAt(0).toUpperCase()}</span>
                  <div className="fp-comment-body">
                    <div className="fp-comment-meta"><b>{c.author}</b>{c.createdAt && <span>{timeAgo(c.createdAt)}</span>}</div>
                    <p>{c.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="fp-thread-empty">No comments yet — be the first to reply.</p>
          )}

          {user ? (
            <div className="fp-compose">
              <span className="fp-comment-avatar">{(user.name || 'U').charAt(0).toUpperCase()}</span>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Write a comment…"
                rows={1}
                maxLength={1000}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postComment() }}
              />
              <button type="button" className="fp-comment-send" onClick={postComment} disabled={posting || !commentText.trim()}>
                <Send size={13} strokeWidth={1.5} /> {posting ? 'Posting…' : 'Reply'}
              </button>
            </div>
          ) : (
            <div className="fp-compose-locked">
              <LogIn size={14} strokeWidth={1.5} />
              <span>{<Link href="/login">Sign in</Link>} to react and join the conversation.</span>
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .fp-card{background:#fff;border:1px solid var(--f-line);border-radius:16px;padding:15px 17px;box-shadow:var(--f-shadow)}
        .fp-card.priority{border-color:rgba(212,175,55,.45);background:linear-gradient(150deg,#FDF7E3,#fff)}
        .fp-priority{display:inline-flex;align-items:center;gap:6px;font-family:var(--f-mono);font-size:8.5px;letter-spacing:.12em;color:var(--f-gold-deep);background:rgba(212,175,55,.12);border-radius:99px;padding:4px 10px;margin-bottom:10px;font-weight:600}
        .fp-head{display:flex;align-items:center;gap:10px}
        .fp-id{flex:1;min-width:0}
        .fp-meta{display:flex;gap:9px;font-size:10.5px;color:var(--f-slate-dim);margin-top:1px;flex-wrap:wrap;align-items:center}
        .fp-type{display:inline-flex;align-items:center;gap:4px;font-family:var(--f-mono);font-size:8.5px;letter-spacing:.1em;color:var(--f-green);font-weight:600}
        .fp-follow{border:1px solid var(--f-line-strong);border-radius:99px;background:#fff;color:var(--f-green-deep);font-size:11.5px;font-weight:600;padding:6px 14px;cursor:pointer;font-family:inherit;transition:all .2s;white-space:nowrap}
        .fp-follow.on{background:rgba(14,124,97,.08);border-color:rgba(14,124,97,.3)}
        .fp-actions{display:flex;align-items:center;gap:6px;margin-top:12px;padding-top:11px;border-top:1px solid var(--f-line)}
        .fp-actions button,.fp-card :global(.fp-action-link){display:inline-flex;align-items:center;gap:6px;border:none;background:none;color:var(--f-slate);font-size:12px;font-weight:500;padding:6px 10px;border-radius:9px;cursor:pointer;font-family:inherit;text-decoration:none;transition:all .15s}
        .fp-actions button:hover,.fp-card :global(.fp-action-link:hover){background:rgba(14,124,97,.06);color:var(--f-green)}
        .fp-actions button.on{color:#D14343}
        .fp-card :global(.fp-avatar){width:38px;height:38px;border-radius:50%;background:linear-gradient(135deg,var(--f-green),var(--f-green-dark));color:#fff;font-weight:700;font-size:15px;display:grid;place-items:center;flex:none;text-decoration:none}
        .fp-card :global(.fp-name){display:inline-flex;align-items:center;gap:5px;font-family:var(--f-display);font-weight:600;font-size:13.5px;color:var(--f-navy);text-decoration:none}
        .fp-card :global(.fp-name svg){color:var(--f-teal)}
        .fp-card :global(.fp-body){display:block;margin-top:11px;text-decoration:none;color:inherit}
        .fp-card :global(.fp-body h3){font-family:var(--f-display);font-weight:600;font-size:14.5px;color:var(--f-navy);line-height:1.35}
        .fp-card :global(.fp-body p){font-size:12.5px;color:var(--f-slate);margin-top:4px;line-height:1.55}
        .fp-card :global(.fp-photo){display:block;margin-top:11px;border-radius:12px;overflow:hidden}
        .fp-card :global(.fp-photo img){width:100%;height:240px;object-fit:cover;display:block;background:#eceae2}
        .fp-spacer{flex:1}
        .fp-actions button:disabled{opacity:.55;cursor:default}
        .fp-actions button.on-c{color:var(--f-green)}
        .fp-card :global(.fp-open){font-size:11.5px;font-weight:600;color:var(--f-green);text-decoration:none}
        .fp-actions button.fp-delete{color:var(--f-slate)}
        .fp-actions button.fp-delete:hover{background:rgba(209,67,67,.08);color:#D14343}
        .fp-actions button.fp-moderate{color:#B45309;font-weight:600}
        .fp-actions button.fp-moderate:hover{background:rgba(180,83,9,.09);color:#92400E}
        .fp-actions button.fp-moderate-del{color:var(--f-slate-dim);font-size:11px}
        .fp-actions button.fp-moderate-del:hover{background:rgba(209,67,67,.08);color:#D14343}
        .fp-edit textarea{width:100%;box-sizing:border-box;border:1px solid var(--f-line);border-radius:10px;padding:9px 11px;font-size:13px;line-height:1.5;font-family:inherit;color:var(--f-ink);resize:vertical;min-height:62px;background:#fff}
        .fp-edit-actions{display:flex;gap:8px;margin-top:8px}
        .fp-edit button.fp-edit-save{background:var(--f-green);color:#fff;border:none;border-radius:8px;padding:6px 15px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit}
        .fp-edit button.fp-edit-save:disabled{opacity:.55;cursor:default}
        .fp-edit button.fp-edit-cancel{background:none;border:1px solid var(--f-line);border-radius:8px;padding:6px 15px;font-size:12px;font-weight:500;color:var(--f-slate);cursor:pointer;font-family:inherit}
        .fp-blood{margin-top:11px}
        .fp-blood-facts{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px}
        .fp-blood-group{font-family:var(--f-display);font-weight:800;font-size:20px;color:#B91C1C;background:rgba(185,28,28,.08);border:1px solid rgba(185,28,28,.22);border-radius:10px;padding:2px 12px;line-height:1.4}
        .fp-blood-chip{font-size:11px;font-weight:600;color:var(--f-slate);background:rgba(11,19,43,.05);border-radius:99px;padding:4px 10px}
        .fp-blood-chip.urgent{color:#B91C1C;background:rgba(185,28,28,.09)}
        .fp-blood-hospital{font-size:12.5px;font-weight:600;color:var(--f-navy);margin:0 0 4px}
        .fp-blood-detail{font-size:12.5px;color:var(--f-slate);line-height:1.55;margin:0 0 10px}
        .fp-blood-donate{display:block;width:100%;margin-top:6px;background:#B91C1C;color:#fff;border:none;border-radius:10px;padding:10px;font-size:13px;font-weight:700;font-family:inherit;cursor:pointer}
        .fp-blood-donate:disabled{opacity:.55;cursor:not-allowed}
        .fp-blood-sub{display:block;text-align:center;font-size:10.5px;color:var(--f-slate-dim);margin-top:6px}
        .fp-blood-phone{text-decoration:none;text-align:center;line-height:1.5}
        .fp-blood-error{display:block;text-align:center;font-size:11px;color:#B91C1C;margin-top:6px}
        .fp-blood-resolve{margin-top:9px;background:none;border:1px solid var(--f-line-strong);border-radius:9px;color:var(--f-green-deep);font-size:12px;font-weight:600;padding:6px 12px;cursor:pointer;font-family:inherit}
        .fp-blood-resolve:disabled{opacity:.55;cursor:default}
        /* Emergency — the most prominent card: a red-tinted body with a solid alert strip. */
        .fp-emergency{margin-top:11px;border:1px solid rgba(185,28,28,.28);background:linear-gradient(160deg,#FEF2F2,#fff);border-radius:12px;padding:11px 12px}
        .fp-emergency-strip{display:inline-flex;align-items:center;gap:6px;background:#B91C1C;color:#fff;font-size:10.5px;font-weight:800;letter-spacing:.04em;border-radius:7px;padding:3px 9px;margin-bottom:9px}
        .fp-emergency-facts{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px}
        .fp-emergency-type{font-family:var(--f-display);font-weight:800;font-size:16px;color:#7F1D1D}
        .fp-emergency-chip{display:inline-flex;align-items:center;gap:4px;font-size:11px;font-weight:600;color:var(--f-slate);background:rgba(11,19,43,.05);border-radius:99px;padding:4px 10px}
        .fp-emergency-detail{font-size:12.5px;color:var(--f-slate);line-height:1.55;margin:0 0 10px}
        .fp-emergency-call{display:flex;align-items:center;justify-content:center;gap:7px;width:100%;background:#B91C1C;color:#fff;border:none;border-radius:10px;padding:10px;font-size:13px;font-weight:700;font-family:inherit;text-decoration:none}
        .fp-cat-emergency{background:rgba(185,28,28,.12);color:#B91C1C}
        .fp-carpool{margin-top:11px}
        .fp-carpool-dir{display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:700;color:var(--f-green-deep);margin-bottom:6px}
        .fp-carpool-route{display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:15px;font-weight:700;color:var(--f-navy);margin:0 0 6px}
        .fp-carpool-arrow{color:var(--f-slate-dim);font-weight:400}
        .fp-carpool-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:6px}
        .fp-carpool-chip{font-size:11px;font-weight:600;color:var(--f-slate);background:rgba(11,19,43,.05);border-radius:99px;padding:4px 10px}
        .fp-carpool-notes{font-size:12.5px;color:var(--f-slate);line-height:1.55;margin:0 0 8px}
        .fp-carpool-contact{display:inline-block;font-size:12.5px;font-weight:600;color:var(--f-green-deep);text-decoration:none}
        .fp-adhan{margin-top:11px}
        .fp-adhan-card{display:flex;align-items:center;gap:11px;text-decoration:none;background:rgba(14,124,97,.06);border:1px solid rgba(14,124,97,.22);border-radius:12px;padding:11px 13px}
        .fp-adhan-card:hover{background:rgba(14,124,97,.11)}
        .fp-adhan-icon{flex:none;width:38px;height:38px;display:grid;place-items:center;border-radius:99px;background:var(--f-green-deep);color:#fff}
        .fp-adhan-main{display:flex;flex-direction:column;gap:2px;min-width:0;flex:1}
        .fp-adhan-label{font-size:11px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:var(--f-green-deep)}
        .fp-adhan-caption{font-size:13px;color:var(--f-navy);line-height:1.4;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}
        .fp-adhan-play{flex:none;display:inline-flex;align-items:center;gap:5px;font-size:12.5px;font-weight:700;color:#fff;background:var(--f-green-deep);border-radius:99px;padding:6px 12px}
        .feedp-carpool-fields{display:flex;flex-direction:column;gap:8px;margin-top:10px}
        .feedp-carpool-dirs{display:flex;gap:6px;flex-wrap:wrap}
        .feedp-carpool-fields input{border:1px solid var(--f-line-strong);border-radius:9px;padding:8px 11px;font-size:12.5px;font-family:inherit;color:var(--f-ink);background:#fff}
        .fp-event{margin-top:11px}
        .fp-event-title{font-size:15.5px;font-weight:800;color:var(--f-navy);margin:0 0 7px;line-height:1.35}
        .fp-event-meta{display:flex;flex-direction:column;gap:5px;margin-bottom:7px}
        .fp-event-when,.fp-event-venue{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;font-weight:600;color:var(--f-slate)}
        .fp-event-when svg,.fp-event-venue svg{color:var(--f-green-deep);flex-shrink:0}
        .fp-event-notes{font-size:12.5px;color:var(--f-slate);line-height:1.55;margin:0 0 8px}
        .fp-event-rsvp{display:inline-block;font-size:12.5px;font-weight:600;color:var(--f-green-deep);text-decoration:none}
        .fp-sale{margin-top:11px}
        .fp-sale-head{display:flex;align-items:baseline;justify-content:space-between;gap:10px;margin-bottom:6px}
        .fp-sale-title{display:inline-flex;align-items:center;gap:6px;font-size:15px;font-weight:800;color:var(--f-navy);margin:0;line-height:1.3}
        .fp-sale-title svg{color:var(--f-green-deep);flex-shrink:0}
        .fp-sale-price{font-size:15px;font-weight:800;color:var(--f-green-deep);white-space:nowrap}
        .fp-sale-noprice{font-size:12.5px;font-weight:600;color:var(--f-slate-dim)}
        .fp-sale-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:6px}
        .fp-sale-chip{font-size:11px;font-weight:600;color:var(--f-slate);background:rgba(11,19,43,.05);border-radius:99px;padding:4px 10px}
        .fp-sale-loc{display:inline-flex;align-items:center;gap:4px;font-size:12px;font-weight:600;color:var(--f-slate)}
        .fp-sale-notes{font-size:12.5px;color:var(--f-slate);line-height:1.55;margin:0 0 8px}
        .fp-sale-contact{display:inline-block;font-size:12.5px;font-weight:600;color:var(--f-green-deep);text-decoration:none}
        .feedp-sale-fields{display:flex;flex-direction:column;gap:8px;margin-top:10px}
        .feedp-sale-fields input,.feedp-sale-fields select{border:1px solid var(--f-line-strong);border-radius:9px;padding:8px 11px;font-size:12.5px;font-family:inherit;color:var(--f-ink);background:#fff}

        .fp-thread{margin-top:11px;padding-top:11px;border-top:1px solid var(--f-line)}
        .fp-comments{list-style:none;display:grid;gap:11px;margin:0 0 11px;padding:0}
        .fp-comment{display:flex;gap:9px;align-items:flex-start}
        .fp-comment-avatar{width:27px;height:27px;border-radius:50%;background:rgba(14,124,97,.1);color:var(--f-green);font-size:11px;font-weight:700;display:grid;place-items:center;flex:none}
        .fp-comment-body{flex:1;min-width:0;background:#FBFAF6;border:1px solid var(--f-line);border-radius:11px;padding:7px 11px}
        .fp-comment-meta{display:flex;gap:8px;align-items:baseline}
        .fp-comment-meta b{font-family:var(--f-display);font-weight:600;font-size:12px;color:var(--f-navy)}
        .fp-comment-meta span{font-size:10.5px;color:var(--f-slate-dim)}
        .fp-comment-body p{font-size:12.5px;color:var(--f-slate);margin-top:2px;line-height:1.5;white-space:pre-wrap;word-break:break-word}
        .fp-thread-empty{font-size:12px;color:var(--f-slate);margin:0 0 11px;line-height:1.5}
        .fp-compose{display:flex;gap:9px;align-items:flex-start}
        .fp-compose textarea{flex:1;border:1px solid var(--f-line-strong);border-radius:11px;padding:8px 11px;font-family:inherit;font-size:12.5px;color:var(--f-ink);resize:vertical;outline:none;background:#fff;min-height:38px}
        .fp-compose textarea:focus{border-color:var(--f-green)}
        .fp-card :global(.fp-comment-send){display:inline-flex;align-items:center;gap:5px;border:none;background:linear-gradient(135deg,var(--f-green),var(--f-green-deep));color:#fff;font-size:11.5px;font-weight:600;border-radius:9px;padding:8px 13px;cursor:pointer;font-family:inherit;white-space:nowrap;align-self:flex-start}
        .fp-card :global(.fp-comment-send:disabled){opacity:.55;cursor:default}
        .fp-compose-locked{display:flex;align-items:center;gap:7px;font-size:12px;color:var(--f-slate)}
        .fp-card :global(.fp-compose-locked a){color:var(--f-green);font-weight:600;text-decoration:none}

        /* user posts — distinct from masjid cards: no badge, teal-tinted avatar, plain name */
        .fp-avatar-user{background:linear-gradient(135deg,var(--f-teal),var(--f-green))}
        .fp-avatar-img{width:100%;height:100%;object-fit:cover;border-radius:50%;display:block}
        .fp-name-user{font-family:var(--f-display);font-weight:600;font-size:13.5px;color:var(--f-navy)}
        .fp-name-row{display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap}
        .fp-role{font-size:9.5px;font-weight:800;letter-spacing:0.4px;text-transform:uppercase;padding:1px 7px;border-radius:999px;line-height:1.6;white-space:nowrap}
        .fp-role-admin{color:#065f46;background:#d1fae5;border:1px solid #a7f3d0}
        .fp-role-mod{color:#3730a3;background:#e0e7ff;border:1px solid #c7d2fe}
        .fp-card :global(.fp-body-user){display:block;margin-top:11px}
        .fp-card :global(.fp-body-user p){font-size:13px;color:var(--f-ink);line-height:1.55;white-space:pre-wrap;word-break:break-word}
        .fp-cat{display:inline-flex;align-items:center;font-family:var(--f-mono);font-size:8.5px;letter-spacing:.08em;font-weight:700;text-transform:uppercase;border-radius:99px;padding:3px 8px;background:rgba(14,124,97,.1);color:var(--f-green-deep)}
        .fp-cat-dua{background:rgba(212,175,55,.16);color:var(--f-gold-deep)}
        .fp-cat-help{background:rgba(209,67,67,.12);color:#B23232}
        .fp-actions button.ameen.on{color:var(--f-gold-deep)}
      `}</style>
    </article>
  )
}

/* PERF P2: first feed page is server-fed — real posts in the initial HTML */
export default function FeedClient({ initialItems, initialNextOffset, initialUrgent = [], bloodEnabled = false, achievementsEnabled = false, carpoolEnabled = false, eventEnabled = false, saleEnabled = false, accommodationEnabled = false, emergencyEnabled = false, campaignsEnabled = false }: { initialItems: FeedItem[]; initialNextOffset: number | null; initialUrgent?: UrgentItem[]; bloodEnabled?: boolean; achievementsEnabled?: boolean; carpoolEnabled?: boolean; eventEnabled?: boolean; saleEnabled?: boolean; accommodationEnabled?: boolean; emergencyEnabled?: boolean; campaignsEnabled?: boolean }) {
  const { follows, ready } = useSocial()
  const [user, setUser] = useState<any>(null)
  // Feed SCOPE (server-filtered): 'all' = the public stream, 'mine' = the caller's own posts (server
  // resolves "me" from the session; Phase 0 foundation). Distinct from catFilter, which narrows the
  // loaded scope client-side. activeTabRef mirrors it so loadPage reads the live value without a dep.
  const [activeTab, setActiveTab] = useState<'all' | 'mine' | 'nearby'>('all')
  const activeTabRef = useRef<'all' | 'mine' | 'nearby'>('all')
  // Nearby (Phase 4 Slice 2): the viewer's captured coords (ref so loadPage reads the live value), plus
  // two DISTINCT honest states — `locating` while GPS is being fetched, `needsLocation` when the viewer
  // denied/has no fix (→ "Enable location" prompt, NOT an empty list, NOT the all-posts list).
  const viewerCoordsRef = useRef<{ latitude: number; longitude: number } | null>(null)
  const [locating, setLocating] = useState(false)
  const [needsLocation, setNeedsLocation] = useState(false)
  // Category filter is now SERVER-SIDE too (Phase 3b): it composes with the scope tab in one query,
  // so a category shows its COMPLETE set (not just matches within the loaded page). catFilterRef holds
  // the live value so loadPage reads it without a dep. 'all' = no category filter.
  const [catFilter, setCatFilter] = useState<string>('all')
  const catFilterRef = useRef<string>('all')
  const [items, setItems] = useState<FeedItem[]>(initialItems)
  const [nextOffset, setNextOffset] = useState<number | null>(initialNextOffset)
  const [loading, setLoading] = useState(initialItems.length === 0)
  const [urgent, setUrgent] = useState<UrgentItem[]>(initialUrgent)
  // Right-rail widgets now self-fetch REAL data from their own sources (the feed no
  // longer carries campaign/listing items).
  const [railCampaigns, setRailCampaigns] = useState<Array<{ id: string; title: string; sub: string }>>([])
  const [railMasjids, setRailMasjids] = useState<Array<{ slug: string; name: string; city: string }>>([])
  // compose box
  const [composer, setComposer] = useState('')
  const [composeCat, setComposeCat] = useState<string>('general')
  const [composeUrgent, setComposeUrgent] = useState(false)
  // Blood-request structured compose fields (only used when composeCat==='blood').
  const [bloodGroup, setBloodGroup] = useState<string>('')
  const [bloodHospital, setBloodHospital] = useState('')
  const [bloodUnits, setBloodUnits] = useState('')
  const [bloodExpiresIn, setBloodExpiresIn] = useState<number>(2)
  const [bloodPhone, setBloodPhone] = useState('')
  // Carpool structured compose fields (only used when composeCat==='carpool').
  const [carpoolDir, setCarpoolDir] = useState<'offering' | 'seeking'>('offering')
  const [carpoolFrom, setCarpoolFrom] = useState('')
  const [carpoolTo, setCarpoolTo] = useState('')
  const [carpoolWhen, setCarpoolWhen] = useState('')
  const [carpoolSeats, setCarpoolSeats] = useState('')
  const [carpoolContact, setCarpoolContact] = useState('')
  // Event structured compose fields (only used when composeCat==='event'). event_at is REQUIRED.
  const [eventTitle, setEventTitle] = useState('')
  const [eventWhen, setEventWhen] = useState('')
  const [eventVenue, setEventVenue] = useState('')
  const [eventRsvp, setEventRsvp] = useState('')
  // Sale structured compose fields (only used when composeCat==='sale'). price is OPTIONAL, entered in
  // RUPEES; the SERVER converts to paise (single source of truth). Blank price → null (honest-empty).
  const [saleTitle, setSaleTitle] = useState('')
  const [salePrice, setSalePrice] = useState('')
  const [saleCondition, setSaleCondition] = useState('')
  const [salePickup, setSalePickup] = useState('')
  const [saleContact, setSaleContact] = useState('')
  // Accommodation structured compose fields (only used when composeCat==='accommodation'). rent is
  // OPTIONAL, entered in RUPEES; the SERVER converts to paise (same as Sale). Blank rent → null.
  const [accDir, setAccDir] = useState<'offering' | 'seeking'>('offering')
  const [accArea, setAccArea] = useState('')
  const [accRent, setAccRent] = useState('')
  const [accRooms, setAccRooms] = useState('')
  const [accAvailable, setAccAvailable] = useState('')
  const [accContact, setAccContact] = useState('')
  // Emergency structured compose fields (only used when composeCat==='emergency'). contact is shown
  // directly on the card (public — fastest path to the poster); expiry is in HOURS (incident-scale).
  const [emIncidentType, setEmIncidentType] = useState('')
  const [emArea, setEmArea] = useState('')
  const [emCity, setEmCity] = useState('')
  const [emSeverity, setEmSeverity] = useState('')
  const [emContact, setEmContact] = useState('')
  const [emExpiresIn, setEmExpiresIn] = useState<number>(24)
  const [posting, setPosting] = useState(false)
  const [composeError, setComposeError] = useState<string | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const fetchSeq = useRef(0)

  useEffect(() => {
    fetch('/api/auth/me').then((r) => r.json()).then((d) => { if (d?.user) setUser(d.user) }).catch(() => {})
  }, [])

  // Rail widgets — real data from the campaign + masjid sources (not the feed).
  useEffect(() => {
    fetch('/api/campaigns?status=active&limit=3')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const list = Array.isArray(d?.campaigns) ? d.campaigns : []
        setRailCampaigns(list.slice(0, 3).map((c: any) => ({
          id: String(c.id), title: String(c.titleEnglish || c.title || 'Campaign'),
          sub: [c.masjid, c.city].filter(Boolean).join(' · '),
        })))
      }).catch(() => {})
    fetch('/api/masjids?limit=3')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const list = Array.isArray(d?.masjids) ? d.masjids : []
        setRailMasjids(list.slice(0, 3).map((m: any) => ({
          slug: String(m.slug || ''), name: String(m.nameEnglish || m.name || 'Masjid'), city: String(m.city || ''),
        })).filter((m: { slug: string }) => m.slug))
      }).catch(() => {})
  }, [])

  const loadPage = useCallback(async (offset: number, replace: boolean) => {
    const seq = ++fetchSeq.current
    if (replace) setLoading(true)
    try {
      // Phase 3: the scope tab drives the server filter. activeTabRef holds the live value so this
      // callback needs no dep on it (every caller — infinite scroll, compose/delete/moderate reload —
      // always fetches the CURRENT scope). 'mine' + no session yields an empty list server-side.
      // Phase 4: on the nearby tab, pass the viewer's captured coords + radius so the server distance-
      // filters. (changeTab only reaches loadPage for nearby AFTER a fix is captured, so coords exist.)
      const geo = activeTabRef.current === 'nearby' && viewerCoordsRef.current
        ? `&lat=${viewerCoordsRef.current.latitude}&lng=${viewerCoordsRef.current.longitude}&radiusKm=5` : ''
      const res = await fetch(`/api/feed?offset=${offset}&limit=8&tab=${activeTabRef.current}&category=${catFilterRef.current}${geo}`)
      const d = await res.json()
      if (seq !== fetchSeq.current) return
      // Defensive: if the server signals it needs a location (nearby w/o coords), show that honest state.
      if (d?.needsLocation) { setNeedsLocation(true); setItems([]); setNextOffset(null); return }
      setItems((prev) => (replace ? d.items : [...prev, ...d.items]))
      setNextOffset(d.nextOffset)
      // urgentItems only ride the first page (offset===0 / replace loads).
      if (replace && Array.isArray(d.urgentItems)) setUrgent(d.urgentItems)
    } catch {
      if (seq === fetchSeq.current) setNextOffset(null)
    } finally {
      if (seq === fetchSeq.current) setLoading(false)
    }
  }, [])

  // Nearby capture (Phase 4): request the viewer's GPS (reusing the Slice-1 captureCoords helper). On
  // success → fetch the nearby page; on denial/timeout → the honest "Enable location" state (no fetch,
  // NOT an empty list). Also used by the retry affordance in that state.
  const captureNearby = useCallback(async () => {
    setNeedsLocation(false)
    setLocating(true)
    const coords = await captureCoords()
    setLocating(false)
    if (!coords) { viewerCoordsRef.current = null; setNeedsLocation(true); return }
    viewerCoordsRef.current = coords
    setItems([])
    setNextOffset(null)
    loadPage(0, true)
  }, [loadPage])

  // Switch the feed SCOPE (All / My Posts / Nearby). Mirrors the compose/delete reset: clear the list +
  // pagination, then refetch page 0. Nearby first captures the viewer's location (captureNearby); the
  // category chips are untouched (they narrow whatever scope is loaded).
  const changeTab = useCallback((tab: 'all' | 'mine' | 'nearby') => {
    if (tab === activeTabRef.current) return
    activeTabRef.current = tab
    setActiveTab(tab)
    setItems([])
    setNextOffset(null)
    setNeedsLocation(false)
    if (tab === 'nearby') { void captureNearby(); return }   // GPS-gated; captureNearby fetches on success
    loadPage(0, true)
  }, [loadPage, captureNearby])

  // Category filter — now a SERVER refetch (Phase 3b), composing with the current scope tab. Resets
  // the list + pagination and refetches page 0 with category=cat (+ the live tab). Same reset shape
  // as changeTab; loadPage reads the new value via catFilterRef, so no stale closure.
  const changeCategory = useCallback((cat: string) => {
    if (cat === catFilterRef.current) return
    catFilterRef.current = cat
    setCatFilter(cat)
    setItems([])
    setNextOffset(null)
    loadPage(0, true)
  }, [loadPage])

  // Author self-delete: optimistically drop the post, then soft-delete it server-side
  // (status='deleted'). On failure, reload to restore the true feed and surface an HONEST error —
  // never a silent drop. The DELETE endpoint enforces the author gate; the UI only shows this to
  // the author.
  const deletePost = useCallback(async (id: string) => {
    setItems((prev) => prev.filter((p) => p.id !== id))
    try {
      const res = await fetch(`/api/community-feed/${id}`, { method: 'DELETE', credentials: 'same-origin' })
      if (res.status === 401) { window.location.href = '/login'; return }
      if (!res.ok) { window.alert('Could not delete the post. Please try again.'); await loadPage(0, true) }
    } catch {
      window.alert('Could not delete the post. Please try again.'); await loadPage(0, true)
    }
  }, [loadPage])

  // Author text-edit: optimistically apply the new body, then PATCH (text-only). On failure,
  // reload to restore the true body + surface an HONEST error. Returns success so the card can
  // leave edit mode only when the save actually landed.
  const editPost = useCallback(async (id: string, bodyText: string): Promise<boolean> => {
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, body: bodyText } : p)))
    try {
      const res = await fetch(`/api/community-feed/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ bodyText }),
      })
      if (res.status === 401) { window.location.href = '/login'; return false }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        window.alert(d?.error || 'Could not edit the post. Please try again.')
        await loadPage(0, true)
        return false
      }
      return true
    } catch {
      window.alert('Could not edit the post. Please try again.')
      await loadPage(0, true)
      return false
    }
  }, [loadPage])

  // Moderator action on ANY post — reuses the existing admin endpoint (remove→'removed' reversible,
  // delete→'deleted' decisive). Optimistically drop it; reload + honest alert on failure (incl. a
  // 403 if the caller isn't actually a moderator). The server is the real gate; the UI just offers it.
  const moderatePost = useCallback(async (id: string, action: 'remove' | 'delete') => {
    setItems((prev) => prev.filter((p) => p.id !== id))
    try {
      const res = await fetch('/api/admin/feed/reported', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id, action }),
      })
      if (res.status === 401 || res.status === 403) { window.alert('You are not authorised to moderate this post.'); await loadPage(0, true); return }
      if (!res.ok) { window.alert('Could not moderate the post. Please try again.'); await loadPage(0, true) }
    } catch {
      window.alert('Could not moderate the post. Please try again.'); await loadPage(0, true)
    }
  }, [loadPage])

  const firstRun = useRef(true)
  useEffect(() => {
    if (firstRun.current && initialItems.length) { firstRun.current = false; return }
    firstRun.current = false
    loadPage(0, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPage])

  // Refresh-on-focus / on-visibility (web parity with the mobile feed's focus+resume refresh). NOT
  // live polling — no interval; fires only when the tab becomes visible again or the window regains
  // focus (covers PWA app-resume too). Guards against churn + a mid-read jump:
  //   • COOLDOWN — skip if a page-0 load happened within FOCUS_REFRESH_COOLDOWN_MS.
  //   • SCROLL — only auto-refresh when the reader is at/near the TOP of the feed; if they've scrolled
  //     into it mid-read, we leave their position untouched (a replace would shift content under them).
  // Reuses loadPage(0, true) — the SAME setter path (fetchSeq guard makes it safe against a concurrent
  // scroll load), and it never blanks the list (the skeleton shows only when items are empty).
  const lastFocusRefreshRef = useRef(Date.now())
  useEffect(() => {
    const maybeRefresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      if (Date.now() - lastFocusRefreshRef.current < FOCUS_REFRESH_COOLDOWN_MS) return
      if (typeof window !== 'undefined' && window.scrollY > 300) return // mid-read → don't disturb
      lastFocusRefreshRef.current = Date.now()
      loadPage(0, true)
    }
    document.addEventListener('visibilitychange', maybeRefresh)
    window.addEventListener('focus', maybeRefresh)
    return () => {
      document.removeEventListener('visibilitychange', maybeRefresh)
      window.removeEventListener('focus', maybeRefresh)
    }
  }, [loadPage])

  /* infinite scroll */
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || nextOffset === null) return
    const io = new IntersectionObserver((es) => {
      if (es[0].isIntersecting && nextOffset !== null && !loading) loadPage(nextOffset, false)
    }, { rootMargin: '400px' })
    io.observe(el)
    return () => io.disconnect()
  }, [nextOffset, loading, loadPage])

  // Phase 3b: category filtering is now SERVER-SIDE (composed with the scope tab, applied before
  // LIMIT/OFFSET), so `items` is already the complete filtered set — no client-side narrowing.
  const visible = items

  const submitPost = useCallback(async () => {
    if (posting) return
    const text = composer.trim()
    const isBlood = composeCat === 'blood'
    const isEmergencyC = composeCat === 'emergency'
    const isCarpoolC = composeCat === 'carpool'
    const isEventC = composeCat === 'event'
    const isSaleC = composeCat === 'sale'
    const isAccommodationC = composeCat === 'accommodation'
    // Structured types carry content in fields (text optional); everything else needs text.
    if (!isBlood && !isEmergencyC && !isCarpoolC && !isEventC && !isSaleC && !isAccommodationC && !text) return
    if (isBlood) {
      if (!bloodGroup) { setComposeError('Choose a blood group.'); return }
      if (!bloodHospital.trim()) { setComposeError('Enter the hospital / blood bank.'); return }
      if (!bloodPhone.trim()) { setComposeError('Enter a contact number.'); return }
    }
    if (isEmergencyC) {
      if (!emIncidentType) { setComposeError('Choose an incident type.'); return }
      if (!emContact.trim()) { setComposeError('Enter a contact number.'); return }
    }
    if (isCarpoolC) {
      if (!carpoolFrom.trim()) { setComposeError('Enter a "from" location.'); return }
      if (!carpoolTo.trim()) { setComposeError('Enter a "to" location.'); return }
    }
    if (isEventC) {
      if (!eventTitle.trim()) { setComposeError('Enter an event title.'); return }
      if (!eventWhen) { setComposeError('Choose the event date and time.'); return }
      if (!eventVenue.trim()) { setComposeError('Enter a venue.'); return }
    }
    if (isSaleC) {
      if (!saleTitle.trim()) { setComposeError('Enter an item title.'); return }
      // price is OPTIONAL — no client validation beyond the server's; blank is allowed.
    }
    if (isAccommodationC) {
      if (!accArea.trim()) { setComposeError('Enter the area / locality.'); return }
      // rent is OPTIONAL — blank allowed; server validates any amount. direction always set.
    }
    setPosting(true)
    setComposeError(null)
    try {
      const payload: Record<string, unknown> = isBlood
        ? {
            category: 'blood',
            bodyText: text,
            urgent: composeUrgent,
            bloodGroup,
            hospital: bloodHospital.trim(),
            attendantPhone: bloodPhone.trim(),
            expiresIn: bloodExpiresIn,
            unitsNeeded: bloodUnits.trim() ? Number(bloodUnits.trim()) : undefined,
          }
        : isEmergencyC
        ? {
            // The single textarea maps to the structured `description` (not bodyText) so the card shows
            // it once. bodyText is omitted → stored null; the card renders description only.
            category: 'emergency',
            incidentType: emIncidentType,
            area: emArea.trim() || undefined,
            city: emCity.trim() || undefined,
            severity: emSeverity || undefined,
            description: text || undefined,
            contact: emContact.trim(),
            expiresInHours: emExpiresIn,
          }
        : isCarpoolC
        ? {
            category: 'carpool',
            bodyText: text,
            direction: carpoolDir,
            fromLocation: carpoolFrom.trim(),
            toLocation: carpoolTo.trim(),
            travelAt: carpoolWhen ? new Date(carpoolWhen).toISOString() : undefined,
            seats: carpoolSeats.trim() ? Number(carpoolSeats.trim()) : undefined,
            contact: carpoolContact.trim() || undefined,
          }
        : isEventC
        ? {
            category: 'event',
            bodyText: text,
            title: eventTitle.trim(),
            eventAt: eventWhen ? new Date(eventWhen).toISOString() : undefined,
            venue: eventVenue.trim(),
            rsvpContact: eventRsvp.trim() || undefined,
          }
        : isSaleC
        ? {
            category: 'sale',
            bodyText: text,
            title: saleTitle.trim(),
            // 🔴 Send RAW rupees (or omit if blank) — the SERVER converts rupees→paise (single source
            // of truth). Blank price is allowed → server stores null → card shows "Price not listed".
            price: salePrice.trim() ? salePrice.trim() : undefined,
            condition: saleCondition || undefined,
            pickupLocation: salePickup.trim() || undefined,
            contact: saleContact.trim() || undefined,
          }
        : isAccommodationC
        ? {
            category: 'accommodation',
            bodyText: text,
            direction: accDir,
            area: accArea.trim(),
            // 🔴 Rent — SAME as Sale: send RAW rupees (or omit if blank); the SERVER converts to paise.
            // Blank → server stores null → card shows "Rent not listed".
            rent: accRent.trim() ? accRent.trim() : undefined,
            rooms: accRooms.trim() || undefined,
            availableFrom: accAvailable || undefined,   // 'YYYY-MM-DD' from <input type=date>
            contact: accContact.trim() || undefined,
          }
        : { bodyText: text, category: composeCat, urgent: composeCat === 'help' && composeUrgent }
      // Phase 4 Slice 1: auto-tag ONLY the 4 structured types with the poster's location. Best-effort +
      // NON-BLOCKING — captureCoords races GPS against a short timeout and resolves null on denial /
      // error / timeout, so the post ALWAYS proceeds (null coords = simply absent from Nearby later).
      if (isCarpoolC || isEventC || isSaleC || isAccommodationC) {
        const coords = await captureCoords()
        if (coords) { payload.latitude = coords.latitude; payload.longitude = coords.longitude }
      }
      const res = await fetch('/api/community-feed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      })
      if (res.status === 401) { window.location.href = '/login'; return }
      const d = await res.json().catch(() => ({}))
      if (res.ok) {
        setComposer(''); setComposeUrgent(false)
        setBloodGroup(''); setBloodHospital(''); setBloodUnits(''); setBloodPhone(''); setBloodExpiresIn(2)
        setCarpoolDir('offering'); setCarpoolFrom(''); setCarpoolTo(''); setCarpoolWhen(''); setCarpoolSeats(''); setCarpoolContact('')
        setEventTitle(''); setEventWhen(''); setEventVenue(''); setEventRsvp('')
        setSaleTitle(''); setSalePrice(''); setSaleCondition(''); setSalePickup(''); setSaleContact('')
        setAccDir('offering'); setAccArea(''); setAccRent(''); setAccRooms(''); setAccAvailable(''); setAccContact('')
        setEmIncidentType(''); setEmArea(''); setEmCity(''); setEmSeverity(''); setEmContact(''); setEmExpiresIn(24)
        await loadPage(0, true) // post is visible immediately → show it at the top
      } else {
        setComposeError(d?.error || 'Your post could not be created right now.')
      }
    } catch {
      setComposeError('Your post could not be created right now.')
    } finally {
      setPosting(false)
    }
  }, [composer, posting, composeCat, composeUrgent, bloodGroup, bloodHospital, bloodPhone, bloodExpiresIn, bloodUnits, carpoolDir, carpoolFrom, carpoolTo, carpoolWhen, carpoolSeats, carpoolContact, eventTitle, eventWhen, eventVenue, eventRsvp, saleTitle, salePrice, saleCondition, salePickup, saleContact, accDir, accArea, accRent, accRooms, accAvailable, accContact, emIncidentType, emArea, emCity, emSeverity, emContact, emExpiresIn, loadPage])

  const isCommittee = user && ['masjid-admin', 'masjid_admin', 'admin'].includes(user.role)

  return (
    <main className="feedp">
      {/* Canonical hero (DESIGN_SYSTEM §2) — one shared PageHero on every page */}
      <PageHero
        compact
        motif="community"
        eyebrow="Ummah Feed"
        title="What the network is doing, right now."
      />

      <div className="feedp-wrap feedp-layout">
        {/* ── left rail ── */}
        <aside className="feedp-left">
          <KitCard>
            {user ? (
              <div className="feedp-me">
                <span className="feedp-me-avatar">{(user.name || 'U').charAt(0).toUpperCase()}</span>
                <div>
                  <b>{user.name}</b>
                  <small>{isCommittee ? 'Committee account' : 'Community member'}</small>
                </div>
              </div>
            ) : (
              <div className="feedp-signin">
                <UserRound size={18} strokeWidth={1.5} />
                <b>Join the conversation</b>
                <small>Sign in to follow masjids and react to updates.</small>
                <Link href="/login" className="feedp-signin-btn"><LogIn size={13} strokeWidth={1.5} /> Sign in</Link>
              </div>
            )}
          </KitCard>

          <KitCard>
            <div className="feedp-rail-kicker">MASJIDS YOU FOLLOW</div>
            {ready && follows.length ? (
              <div className="feedp-follow-list">
                {follows.slice(0, 6).map((f) => (
                  <Link key={f.id} href={f.slug ? `/masjids/${f.slug}` : '/directory'}>
                    <span className="feedp-mini-avatar">{f.name.charAt(0)}</span> {f.name}
                  </Link>
                ))}
              </div>
            ) : (
              <p className="feedp-rail-empty">Follow masjids from their profiles or the cards in this feed — their updates appear in the Following tab.</p>
            )}
            <Link href="/directory" className="feedp-rail-link">Find masjids →</Link>
          </KitCard>
        </aside>

        {/* ── center column ── */}
        <section className="feedp-center">
          {/* urgent strip — help requests flagged urgent (a GLOBAL lane). Honest-empty: nothing when
              none. Hidden under My Posts, where a national urgent strip would be out of scope. */}
          {activeTab === 'all' && urgent.length > 0 && (
            <div className="feedp-urgent" role="region" aria-label="Urgent help requests">
              {urgent.map((u) => (
                <button key={u.id} type="button" className="feedp-urgent-row" onClick={() => changeCategory('help')}>
                  <AlertTriangle size={15} strokeWidth={2} />
                  <span className="feedp-urgent-body">{u.body}</span>
                  <span className="feedp-urgent-author">{u.author || 'Member'}</span>
                </button>
              ))}
            </div>
          )}

          {/* OPEN composer — any logged-in user posts instantly; guests get a sign-in prompt. */}
          <KitCard className="feedp-composer">
            {user ? (
              <>
                <div className="feedp-composer-row">
                  <span className="feedp-me-avatar">{(user.name || 'M').charAt(0).toUpperCase()}</span>
                  <textarea
                    value={composer}
                    onChange={(e) => setComposer(e.target.value)}
                    placeholder="Ask, recommend, or share…"
                    rows={2}
                    maxLength={2000}
                  />
                </div>
                <div className="feedp-cat-pick" role="group" aria-label="Post category">
                  {COMPOSE_CATEGORIES.map((c) => {
                    const Icon = CATEGORY_ICON[c.id]
                    return (
                    <button
                      key={c.id}
                      type="button"
                      className={'feedp-cat-pill' + (composeCat === c.id ? ' on' : '')}
                      aria-pressed={composeCat === c.id}
                      onClick={() => { setComposeCat(c.id); if (c.id !== 'help') setComposeUrgent(false) }}
                    >
                      {Icon && <Icon size={13} strokeWidth={2} />}{c.label}
                    </button>
                  )})}
                  {/* Blood request — only offered when the bloodRequests flag is ON (dark otherwise). */}
                  {bloodEnabled && (
                    <button
                      type="button"
                      className={'feedp-cat-pill' + (composeCat === 'blood' ? ' on' : '')}
                      aria-pressed={composeCat === 'blood'}
                      onClick={() => { setComposeCat('blood'); setComposeUrgent(false) }}
                    >
                      <Droplet size={13} strokeWidth={2} />Blood request
                    </button>
                  )}
                  {/* Achievement — only offered when the achievements flag is ON (dark otherwise). */}
                  {achievementsEnabled && (
                    <button
                      type="button"
                      className={'feedp-cat-pill' + (composeCat === 'achievement' ? ' on' : '')}
                      aria-pressed={composeCat === 'achievement'}
                      onClick={() => { setComposeCat('achievement'); setComposeUrgent(false) }}
                    >
                      <Trophy size={13} strokeWidth={2} />Achievement
                    </button>
                  )}
                  {/* Carpool — only offered when the feedCarpool flag is ON (dark otherwise). */}
                  {carpoolEnabled && (
                    <button
                      type="button"
                      className={'feedp-cat-pill' + (composeCat === 'carpool' ? ' on' : '')}
                      aria-pressed={composeCat === 'carpool'}
                      onClick={() => { setComposeCat('carpool'); setComposeUrgent(false) }}
                    >
                      <Car size={13} strokeWidth={2} />Carpool
                    </button>
                  )}
                  {/* Event — only offered when the feedEvent flag is ON (dark otherwise). */}
                  {eventEnabled && (
                    <button
                      type="button"
                      className={'feedp-cat-pill' + (composeCat === 'event' ? ' on' : '')}
                      aria-pressed={composeCat === 'event'}
                      onClick={() => { setComposeCat('event'); setComposeUrgent(false) }}
                    >
                      <CalendarDays size={13} strokeWidth={2} />Event
                    </button>
                  )}
                  {/* Items for sale — only offered when the feedSale flag is ON (dark otherwise). */}
                  {saleEnabled && (
                    <button
                      type="button"
                      className={'feedp-cat-pill' + (composeCat === 'sale' ? ' on' : '')}
                      aria-pressed={composeCat === 'sale'}
                      onClick={() => { setComposeCat('sale'); setComposeUrgent(false) }}
                    >
                      <Tag size={13} strokeWidth={2} />For sale
                    </button>
                  )}
                  {/* Accommodation — only offered when the feedAccommodation flag is ON (dark otherwise). */}
                  {accommodationEnabled && (
                    <button
                      type="button"
                      className={'feedp-cat-pill' + (composeCat === 'accommodation' ? ' on' : '')}
                      aria-pressed={composeCat === 'accommodation'}
                      onClick={() => { setComposeCat('accommodation'); setComposeUrgent(false) }}
                    >
                      <BedDouble size={13} strokeWidth={2} />Accommodation
                    </button>
                  )}
                  {/* Emergency — only offered when the feedEmergency flag is ON (dark otherwise). */}
                  {emergencyEnabled && (
                    <button
                      type="button"
                      className={'feedp-cat-pill feedp-cat-pill-emergency' + (composeCat === 'emergency' ? ' on' : '')}
                      aria-pressed={composeCat === 'emergency'}
                      onClick={() => { setComposeCat('emergency'); setComposeUrgent(false) }}
                    >
                      <Siren size={13} strokeWidth={2} />Emergency
                    </button>
                  )}
                </div>
                {/* Achievement honesty hint — shown only for the (flag-gated) achievement category. */}
                {achievementsEnabled && composeCat === 'achievement' && (
                  <small className="feedp-achievement-hint">Share a milestone — no fundraising amounts or unverified figures.</small>
                )}
                {/* Structured blood-request fields — shown only for the (flag-gated) blood category. */}
                {bloodEnabled && composeCat === 'blood' && (
                  <div className="feedp-blood-fields" role="group" aria-label="Blood request details">
                    <div className="feedp-blood-groups" role="group" aria-label="Blood group">
                      {BLOOD_GROUPS.map((g) => (
                        <button
                          key={g}
                          type="button"
                          className={'feedp-cat-pill' + (bloodGroup === g ? ' on' : '')}
                          aria-pressed={bloodGroup === g}
                          onClick={() => setBloodGroup(g)}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={bloodHospital}
                      onChange={(e) => setBloodHospital(e.target.value)}
                      placeholder="Hospital / blood bank"
                      maxLength={200}
                      aria-label="Hospital or blood bank"
                    />
                    <input
                      type="number"
                      value={bloodUnits}
                      onChange={(e) => setBloodUnits(e.target.value)}
                      placeholder="Units needed (optional)"
                      min={1}
                      max={99}
                      aria-label="Units needed"
                    />
                    <label className="feedp-blood-expiry">
                      Expires in
                      <select value={bloodExpiresIn} onChange={(e) => setBloodExpiresIn(Number(e.target.value))} aria-label="Expires in">
                        {BLOOD_EXPIRY_OPTIONS.map((d) => (
                          <option key={d} value={d}>{d === 1 ? '1 day' : `${d} days`}</option>
                        ))}
                      </select>
                    </label>
                    <input
                      type="tel"
                      value={bloodPhone}
                      onChange={(e) => setBloodPhone(e.target.value)}
                      placeholder="Contact number (attendant / family)"
                      maxLength={32}
                      aria-label="Contact number"
                    />
                    <small className="feedp-blood-hint">Revealed only when a donor taps — not shown publicly. Details field above: no diagnosis or private medical detail.</small>
                  </div>
                )}
                {/* Structured carpool fields — shown only for the (flag-gated) carpool category. */}
                {carpoolEnabled && composeCat === 'carpool' && (
                  <div className="feedp-carpool-fields" role="group" aria-label="Carpool details">
                    <div className="feedp-carpool-dirs" role="group" aria-label="Direction">
                      <button type="button" className={'feedp-cat-pill' + (carpoolDir === 'offering' ? ' on' : '')} aria-pressed={carpoolDir === 'offering'} onClick={() => setCarpoolDir('offering')}>Offering a ride</button>
                      <button type="button" className={'feedp-cat-pill' + (carpoolDir === 'seeking' ? ' on' : '')} aria-pressed={carpoolDir === 'seeking'} onClick={() => setCarpoolDir('seeking')}>Looking for a ride</button>
                    </div>
                    <input type="text" value={carpoolFrom} onChange={(e) => setCarpoolFrom(e.target.value)} placeholder="From (e.g. Charminar)" maxLength={120} aria-label="From location" />
                    <input type="text" value={carpoolTo} onChange={(e) => setCarpoolTo(e.target.value)} placeholder="To (e.g. Airport)" maxLength={120} aria-label="To location" />
                    <input type="datetime-local" value={carpoolWhen} onChange={(e) => setCarpoolWhen(e.target.value)} aria-label="Travel date and time (optional)" />
                    <input type="number" value={carpoolSeats} onChange={(e) => setCarpoolSeats(e.target.value)} placeholder="Seats (optional)" min={1} max={20} aria-label="Seats" />
                    <input type="tel" value={carpoolContact} onChange={(e) => setCarpoolContact(e.target.value)} placeholder="Contact number (optional)" maxLength={32} aria-label="Contact number" />
                    <small className="feedp-blood-hint">No fares — the platform handles no money. Arrange any cost directly. Contact is optional and shown publicly if you add it.</small>
                    <small className="feedp-geo-note">📍 This post will be tagged with your location.</small>
                  </div>
                )}
                {/* Structured event fields — shown only for the (flag-gated) event category. */}
                {eventEnabled && composeCat === 'event' && (
                  <div className="feedp-carpool-fields" role="group" aria-label="Event details">
                    <input type="text" value={eventTitle} onChange={(e) => setEventTitle(e.target.value)} placeholder="Event title (e.g. Community Iftar)" maxLength={120} aria-label="Event title" />
                    <input type="datetime-local" value={eventWhen} onChange={(e) => setEventWhen(e.target.value)} aria-label="Event date and time (required)" />
                    <input type="text" value={eventVenue} onChange={(e) => setEventVenue(e.target.value)} placeholder="Venue (e.g. Masjid-e-Quba hall)" maxLength={120} aria-label="Venue" />
                    <input type="tel" value={eventRsvp} onChange={(e) => setEventRsvp(e.target.value)} placeholder="RSVP contact (optional)" maxLength={32} aria-label="RSVP contact" />
                    <small className="feedp-blood-hint">Add a description in the box above. Title, date/time and venue are required. No money — the platform handles no payments. RSVP contact is optional and shown publicly if you add it.</small>
                    <small className="feedp-geo-note">📍 This post will be tagged with your location.</small>
                  </div>
                )}
                {/* Structured items-for-sale fields — shown only for the (flag-gated) sale category. */}
                {saleEnabled && composeCat === 'sale' && (
                  <div className="feedp-sale-fields" role="group" aria-label="Item for sale details">
                    <input type="text" value={saleTitle} onChange={(e) => setSaleTitle(e.target.value)} placeholder="Item title (e.g. Study desk)" maxLength={120} aria-label="Item title" />
                    <input type="number" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} placeholder="Leave blank if not listing a price" min={1} aria-label="Price (₹, optional)" />
                    <select value={saleCondition} onChange={(e) => setSaleCondition(e.target.value)} aria-label="Condition (optional)">
                      <option value="">Condition (optional)</option>
                      <option value="new">New</option>
                      <option value="like_new">Like new</option>
                      <option value="used">Used</option>
                    </select>
                    <input type="text" value={salePickup} onChange={(e) => setSalePickup(e.target.value)} placeholder="Pickup location (optional)" maxLength={120} aria-label="Pickup location" />
                    <input type="tel" value={saleContact} onChange={(e) => setSaleContact(e.target.value)} placeholder="Contact number (optional)" maxLength={32} aria-label="Contact number" />
                    <small className="feedp-blood-hint">Price is your own asking price (₹), or leave it blank. The platform takes no payment and never adds fees — contact is an introduction only. Describe the item in the box above.</small>
                    <small className="feedp-geo-note">📍 This post will be tagged with your location.</small>
                  </div>
                )}
                {/* Structured accommodation fields — shown only for the (flag-gated) accommodation category. */}
                {accommodationEnabled && composeCat === 'accommodation' && (
                  <div className="feedp-sale-fields" role="group" aria-label="Accommodation details">
                    <div className="feedp-carpool-dirs" role="group" aria-label="Direction">
                      <button type="button" className={'feedp-cat-pill' + (accDir === 'offering' ? ' on' : '')} aria-pressed={accDir === 'offering'} onClick={() => setAccDir('offering')}>Offering a place</button>
                      <button type="button" className={'feedp-cat-pill' + (accDir === 'seeking' ? ' on' : '')} aria-pressed={accDir === 'seeking'} onClick={() => setAccDir('seeking')}>Looking for a place</button>
                    </div>
                    <input type="text" value={accArea} onChange={(e) => setAccArea(e.target.value)} placeholder="Area / locality (e.g. Tolichowki)" maxLength={120} aria-label="Area or locality" />
                    <input type="number" value={accRent} onChange={(e) => setAccRent(e.target.value)} placeholder="Rent ₹ / month — leave blank if not listing" min={1} aria-label="Rent (₹, optional)" />
                    <input type="text" value={accRooms} onChange={(e) => setAccRooms(e.target.value)} placeholder="Rooms (optional, e.g. 2BHK)" maxLength={40} aria-label="Rooms" />
                    <label className="feedp-blood-expiry">Available from<input type="date" value={accAvailable} onChange={(e) => setAccAvailable(e.target.value)} aria-label="Available from (optional)" /></label>
                    <input type="tel" value={accContact} onChange={(e) => setAccContact(e.target.value)} placeholder="Contact number (optional)" maxLength={32} aria-label="Contact number" />
                    <small className="feedp-blood-hint">Rent is your own asking rent (₹), or leave it blank. The platform takes no payment and never adds fees — contact is an introduction only. Describe the place in the box above.</small>
                    <small className="feedp-geo-note">📍 This post will be tagged with your location.</small>
                  </div>
                )}
                {/* Structured emergency fields — shown only for the (flag-gated) emergency category. */}
                {emergencyEnabled && composeCat === 'emergency' && (
                  <div className="feedp-sale-fields" role="group" aria-label="Emergency details">
                    <div className="feedp-carpool-dirs" role="group" aria-label="Incident type">
                      {EMERGENCY_INCIDENT_TYPES.map((t) => (
                        <button key={t} type="button" className={'feedp-cat-pill' + (emIncidentType === t ? ' on' : '')} aria-pressed={emIncidentType === t} onClick={() => setEmIncidentType(t)}>{t}</button>
                      ))}
                    </div>
                    <input type="text" value={emArea} onChange={(e) => setEmArea(e.target.value)} placeholder="Area / locality (optional)" maxLength={120} aria-label="Area or locality" />
                    <input type="text" value={emCity} onChange={(e) => setEmCity(e.target.value)} placeholder="City (optional)" maxLength={120} aria-label="City" />
                    <select value={emSeverity} onChange={(e) => setEmSeverity(e.target.value)} aria-label="Severity (optional)">
                      <option value="">Severity (optional)</option>
                      {EMERGENCY_SEVERITIES.map((s) => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                      ))}
                    </select>
                    <label className="feedp-blood-expiry">
                      Expires in
                      <select value={emExpiresIn} onChange={(e) => setEmExpiresIn(Number(e.target.value))} aria-label="Expires in">
                        {EMERGENCY_EXPIRY_OPTIONS.map((h) => (
                          <option key={h} value={h}>{h} hours</option>
                        ))}
                      </select>
                    </label>
                    <input type="tel" value={emContact} onChange={(e) => setEmContact(e.target.value)} placeholder="Contact number (shown directly)" maxLength={32} aria-label="Contact number" />
                    <small className="feedp-blood-hint">Contact is shown DIRECTLY on the card so people can reach you fastest — do not add one you don't want public. Describe the situation in the box above. For life-threatening emergencies, call the official services (112) first.</small>
                  </div>
                )}
                {composeError && <p className="feedp-compose-error">{composeError}</p>}
                <div className="feedp-composer-bar">
                  {(composeCat === 'help' || composeCat === 'blood') && (
                    <button
                      type="button"
                      className={'feedp-urgent-toggle' + (composeUrgent ? ' on' : '')}
                      aria-pressed={composeUrgent}
                      onClick={() => setComposeUrgent((v) => !v)}
                    >
                      <AlertTriangle size={13} strokeWidth={1.8} /> {composeUrgent ? 'Marked urgent' : 'Mark urgent'}
                    </button>
                  )}
                  <small className="feedp-posting-as">Posting as {user.name || 'you'}</small>
                  <span className="fp-spacer" />
                  <button
                    type="button"
                    className="feedp-post-btn"
                    onClick={submitPost}
                    disabled={posting || (composeCat === 'blood' ? (!bloodGroup || !bloodHospital.trim() || !bloodPhone.trim()) : composeCat === 'emergency' ? (!emIncidentType || !emContact.trim()) : composeCat === 'carpool' ? (!carpoolFrom.trim() || !carpoolTo.trim()) : composeCat === 'event' ? (!eventTitle.trim() || !eventWhen || !eventVenue.trim()) : composeCat === 'sale' ? !saleTitle.trim() : composeCat === 'accommodation' ? (!accDir || !accArea.trim()) : !composer.trim())}
                  >
                    <Send size={13} strokeWidth={1.5} /> {posting ? 'Posting…' : 'Post'}
                  </button>
                </div>
              </>
            ) : (
              <div className="feedp-composer-locked">
                <Megaphone size={16} strokeWidth={1.5} />
                <p>Join the conversation.{' '}<Link href="/login">Sign in</Link> to post, ask, or share with the ummah.</p>
              </div>
            )}
          </KitCard>

          {/* Signpost — masjid notices/events moved to their own page (with the Janaza lane). */}
          <div className="feedp-signpost">
            <Megaphone size={13} strokeWidth={1.6} />
            <span>Looking for masjid notices &amp; events? They live on the <Link href="/announcements">Announcements</Link> page.</span>
          </div>

          {/* Feed SCOPE tabs (server-filtered) — All / My Posts / Nearby. My Posts is signed-in-only (a
              guest has no "my posts"); All + Nearby are meaningful for everyone (a guest can tap Nearby
              and grant GPS). Distinct from the category chips below, which narrow the loaded scope. */}
          <div className="feedp-scoperow" role="tablist" aria-label="Feed scope">
            <button type="button" role="tab" className={'feedp-scopetab' + (activeTab === 'all' ? ' on' : '')} aria-selected={activeTab === 'all'} onClick={() => changeTab('all')}>All Posts</button>
            {user && (
              <button type="button" role="tab" className={'feedp-scopetab' + (activeTab === 'mine' ? ' on' : '')} aria-selected={activeTab === 'mine'} onClick={() => changeTab('mine')}>My Posts</button>
            )}
            <button type="button" role="tab" className={'feedp-scopetab' + (activeTab === 'nearby' ? ' on' : '')} aria-selected={activeTab === 'nearby'} onClick={() => changeTab('nearby')}>Nearby</button>
          </div>

          {/* category filter row — client-side narrowing of already-loaded items */}
          <div className="feedp-catrow" role="group" aria-label="Filter by category">
            {FILTER_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className={'feedp-catchip' + (catFilter === c.id ? ' on' : '')}
                aria-pressed={catFilter === c.id}
                onClick={() => changeCategory(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* posts */}
          {activeTab === 'nearby' && locating ? (
            /* Capturing the viewer's GPS — distinct from both empty states. */
            <div className="feedp-nearby-state">
              <MapPin size={22} strokeWidth={1.5} />
              <p className="feedp-nearby-title">Finding posts near you…</p>
            </div>
          ) : activeTab === 'nearby' && needsLocation ? (
            /* HONEST no-location state — NOT an empty list, NOT the all-posts list. */
            <div className="feedp-nearby-state">
              <MapPin size={22} strokeWidth={1.5} />
              <p className="feedp-nearby-title">Enable location to see nearby posts</p>
              <p className="feedp-nearby-sub">Your location is used only to find posts within 5&nbsp;km — it isn’t stored.</p>
              <button type="button" className="feedp-nearby-btn" onClick={() => { void captureNearby() }}>Enable location</button>
            </div>
          ) : loading && !items.length ? (
            <div className="feedp-skeletons">
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="feedp-skeleton" />)}
            </div>
          ) : visible.length ? (
            <div className="feedp-posts">
              {visible.flatMap((item, idx) => [
                /* FS2-D: LinkedIn-style in-feed unit every 7 posts */
                ...(idx > 0 && idx % 7 === 0
                  ? [<AdSlot key={`ad-${idx}`} placement="feed_inline" variant="native" />]
                  : []),
                <PostCard
                  key={item.id}
                  item={item}
                  followed={false}
                  onFollow={() => {}}
                  user={user}
                  bloodEnabled={bloodEnabled}
                  carpoolEnabled={carpoolEnabled}
                  eventEnabled={eventEnabled}
                  saleEnabled={saleEnabled}
                  accommodationEnabled={accommodationEnabled}
                  emergencyEnabled={emergencyEnabled}
                  onResolved={() => loadPage(0, true)}
                  onDelete={deletePost}
                  onEdit={editPost}
                  onModerate={moderatePost}
                />,
              ])}
              {nextOffset !== null && (
                <div ref={sentinelRef} className="feedp-skeleton" aria-hidden />
              )}
            </div>
          ) : (
            <EmptyState
              icon={<Bell size={20} strokeWidth={1.5} />}
              title={activeTab === 'nearby' ? 'No posts near you yet' : activeTab === 'mine' ? 'You haven’t posted yet' : catFilter !== 'all' ? `No ${CATEGORY_LABEL[catFilter] || ''} posts yet` : 'No posts yet'}
              body={activeTab === 'nearby'
                ? 'No location-tagged posts within 5 km right now — check back soon.'
                : activeTab === 'mine'
                ? 'Your posts to the community will appear here.'
                : catFilter !== 'all'
                ? 'Be the first to post in this category — your post appears here instantly.'
                : 'Be the first to ask, recommend, or share with the community — posts appear here instantly.'}
            />
          )}
        </section>

        {/* ── right rail ── */}
        <aside className="feedp-right">
          {/* Real campaign data, fetched from its own source (not the feed). Hidden when empty. */}
          {railCampaigns.length > 0 && (
            <KitCard>
              <div className="feedp-rail-kicker"><TrendingUp size={12} strokeWidth={1.5} /> CAMPAIGN MOMENTUM</div>
              {railCampaigns.map((c) => (
                <Link key={c.id} href={`/campaign/${c.id}`} className="feedp-trend">
                  <b>{c.title}</b>
                  {c.sub && <small>{c.sub}</small>}
                </Link>
              ))}
              {campaignsEnabled && <Link href="/give?tab=campaigns" className="feedp-rail-link">All campaigns →</Link>}
            </KitCard>
          )}

          {railMasjids.length > 0 && (
            <KitCard>
              <div className="feedp-rail-kicker"><MapPin size={12} strokeWidth={1.5} /> NEW ON THE DIRECTORY</div>
              {railMasjids.map((m) => (
                <Link key={m.slug} href={`/masjids/${m.slug}`} className="feedp-trend">
                  <b>{m.name}</b>
                  {m.city && <small>{m.city}</small>}
                </Link>
              ))}
              <Link href="/directory" className="feedp-rail-link">Open directory →</Link>
            </KitCard>
          )}

          <KitCard>
            <div className="feedp-rail-kicker"><Megaphone size={12} strokeWidth={1.5} /> ANNOUNCEMENTS &amp; NOTICES</div>
            <p className="feedp-rail-empty">The scannable list with the priority lane lives on its own page.</p>
            <Link href="/announcements" className="feedp-rail-link">Open announcements →</Link>
          </KitCard>

          {/* sidebar ad — clearly-labeled, below the rail widgets (never above H1) */}
          <AdSlot placement="feed_rail" variant="sidebar" />
        </aside>
      </div>

      <style jsx>{`
        .feedp{background:var(--f-beige);}
        .feedp-wrap{max-width:1280px;margin:0 auto;padding:0 26px}
        /* hero is now the shared <PageHero> — bespoke .feedp-hero CSS removed */
        .feedp-layout{display:grid;grid-template-columns:230px minmax(0,1fr) 270px;gap:18px;align-items:start;padding-bottom:48px}
        .feedp-left,.feedp-right{display:grid;gap:14px;position:sticky;top:78px}
        .feedp-center{display:grid;gap:14px;min-width:0}

        .feedp-me{display:flex;align-items:center;gap:11px}
        .feedp-me b{display:block;font-family:var(--f-display);font-weight:600;font-size:14px;color:var(--f-navy)}
        .feedp-me small{font-size:11px;color:var(--f-slate)}
        .feedp-me-avatar{width:38px;height:38px;border-radius:50%;background:linear-gradient(135deg,var(--f-green),var(--f-teal));color:#fff;font-weight:700;font-size:15px;display:grid;place-items:center;flex:none}
        .feedp-signin{display:grid;gap:6px;justify-items:start;color:var(--f-green)}
        .feedp-signin b{font-family:var(--f-display);font-weight:600;font-size:14px;color:var(--f-navy)}
        .feedp-signin small{font-size:11.5px;color:var(--f-slate);line-height:1.45}
        .feedp :global(.feedp-signin-btn){display:inline-flex;align-items:center;gap:6px;margin-top:6px;background:linear-gradient(135deg,var(--f-green),var(--f-green-deep));color:#fff;font-size:12px;font-weight:600;border-radius:9px;padding:8px 14px;text-decoration:none}

        .feedp-rail-kicker{display:flex;align-items:center;gap:6px;font-family:var(--f-mono);font-size:9px;letter-spacing:.14em;color:var(--f-green);margin-bottom:10px}
        .feedp-follow-list{display:grid;gap:4px}
        .feedp :global(.feedp-follow-list a){display:flex;align-items:center;gap:8px;font-size:12.5px;font-weight:500;color:var(--f-ink);text-decoration:none;padding:6px 7px;border-radius:9px}
        .feedp :global(.feedp-follow-list a:hover){background:rgba(14,124,97,.06)}
        .feedp-mini-avatar{width:24px;height:24px;border-radius:50%;background:rgba(14,124,97,.1);color:var(--f-green);font-size:11px;font-weight:700;display:grid;place-items:center;flex:none}
        .feedp-rail-empty{font-size:11.5px;color:var(--f-slate);line-height:1.5}
        .feedp :global(.feedp-rail-link){display:inline-block;margin-top:10px;font-size:11.5px;font-weight:600;color:var(--f-green);text-decoration:none}
        .feedp :global(.feedp-trend){display:block;padding:8px 0;border-bottom:1px solid var(--f-line);text-decoration:none}
        .feedp :global(.feedp-trend:last-of-type){border-bottom:none}
        .feedp :global(.feedp-trend b){display:block;font-family:var(--f-display);font-weight:600;font-size:12.5px;color:var(--f-navy);line-height:1.35}
        .feedp :global(.feedp-trend small){font-size:11px;color:var(--f-slate)}

        .feedp :global(.feedp-composer){padding:14px 15px}
        .feedp-composer-row{display:flex;gap:11px;align-items:flex-start}
        .feedp-composer-row textarea{flex:1;border:1px solid var(--f-line-strong);border-radius:12px;padding:10px 13px;font-family:inherit;font-size:13px;color:var(--f-ink);resize:vertical;outline:none;background:#FBFAF6}
        .feedp-composer-row textarea:focus{border-color:var(--f-green)}
        .feedp-composer-bar{display:flex;align-items:center;gap:10px;margin-top:10px}
        .feedp-attach{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--f-line);border-radius:9px;background:#fff;color:var(--f-slate);font-size:12px;font-weight:500;padding:7px 11px;cursor:pointer;font-family:inherit}
        .feedp :global(.feedp-post-btn){display:inline-flex;align-items:center;gap:6px;background:linear-gradient(135deg,var(--f-green),var(--f-green-deep));color:#fff;font-size:12px;font-weight:600;border-radius:9px;padding:8px 15px;text-decoration:none;border:none;cursor:pointer;font-family:inherit}
        .feedp :global(.feedp-post-btn:disabled){opacity:.5;cursor:default}
        .feedp-composer-locked{display:flex;gap:10px;align-items:center;color:var(--f-green)}
        .feedp-composer-locked p{font-size:12.5px;color:var(--f-slate);line-height:1.5}
        .feedp :global(.feedp-composer-locked a){color:var(--f-green);font-weight:600;text-decoration:none}

        /* compose: category picker pills + urgent toggle + posting-as line */
        .feedp-cat-pick{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px}
        .feedp-cat-pill{display:inline-flex;align-items:center;gap:5px;border:1px solid var(--f-line-strong);border-radius:99px;background:#fff;color:var(--f-slate);font-size:11.5px;font-weight:600;padding:5px 11px;cursor:pointer;font-family:inherit;transition:all .15s}
        .feedp-cat-pill.on{background:var(--f-green);border-color:var(--f-green);color:#fff}
        .feedp-cat-pill-emergency{border-color:rgba(185,28,28,.4);color:#B91C1C}
        .feedp-cat-pill-emergency.on{background:#B91C1C;border-color:#B91C1C;color:#fff}
        .feedp-compose-error{font-size:11.5px;color:#B23232;margin-top:8px;line-height:1.4}
        .feedp-achievement-hint{display:block;font-size:11px;color:var(--f-slate-dim);margin-top:8px;line-height:1.4}
        .feedp-geo-note{display:block;font-size:11px;font-weight:600;color:var(--f-green-deep);margin-top:6px;line-height:1.4}
        .feedp-urgent-toggle{display:inline-flex;align-items:center;gap:5px;border:1px solid rgba(209,67,67,.4);border-radius:9px;background:#fff;color:#B23232;font-size:11.5px;font-weight:600;padding:6px 11px;cursor:pointer;font-family:inherit}
        .feedp-urgent-toggle.on{background:rgba(209,67,67,.1)}
        .feedp-posting-as{font-size:11px;color:var(--f-slate-dim)}

        /* signpost — masjid notices moved to /announcements */
        .feedp-signpost{display:flex;align-items:center;gap:8px;font-size:12px;color:var(--f-slate);background:#fff;border:1px solid var(--f-line);border-radius:12px;padding:9px 13px}
        .feedp :global(.feedp-signpost a){color:var(--f-green);font-weight:600;text-decoration:none}

        /* urgent strip — red, above the feed */
        .feedp-urgent{display:grid;gap:6px;margin-bottom:4px}
        .feedp-urgent-row{display:flex;align-items:center;gap:9px;width:100%;text-align:left;border:1px solid rgba(209,67,67,.35);background:linear-gradient(150deg,#FDEDED,#fff);border-radius:12px;padding:10px 13px;cursor:pointer;font-family:inherit;color:#B23232}
        .feedp-urgent-body{flex:1;min-width:0;font-size:12.5px;color:var(--f-ink);font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .feedp-urgent-author{font-size:11px;color:var(--f-slate);font-weight:600;white-space:nowrap}

        /* category filter row */
        .feedp-catrow{display:inline-flex;flex-wrap:wrap;gap:6px}
        .feedp-catchip{border:1px solid var(--f-line);border-radius:99px;background:#fff;color:var(--f-slate);font-size:11.5px;font-weight:600;padding:5px 13px;cursor:pointer;font-family:inherit;transition:all .15s}
        .feedp-catchip.on{background:var(--f-navy);border-color:var(--f-navy);color:#fff}
        .feedp-scoperow{display:inline-flex;gap:4px;margin-bottom:10px;background:rgba(11,19,43,.05);border-radius:99px;padding:3px}
        .feedp-scopetab{border:0;border-radius:99px;background:transparent;color:var(--f-slate);font-size:12.5px;font-weight:700;padding:6px 16px;cursor:pointer;font-family:inherit;transition:all .15s}
        .feedp-scopetab.on{background:#fff;color:var(--f-navy);box-shadow:0 1px 3px rgba(11,19,43,.12)}
        .feedp-nearby-state{display:flex;flex-direction:column;align-items:center;text-align:center;gap:8px;padding:40px 24px;color:var(--f-green-deep)}
        .feedp-nearby-title{font-size:15px;font-weight:800;color:var(--f-navy);margin:0}
        .feedp-nearby-sub{font-size:12.5px;color:var(--f-slate);margin:0;max-width:320px;line-height:1.5}
        .feedp-nearby-btn{margin-top:6px;border:0;border-radius:10px;background:var(--f-green);color:#fff;font-size:13px;font-weight:700;padding:9px 18px;cursor:pointer;font-family:inherit}
        .fp-dist{color:var(--f-green-deep);font-weight:700}

        .feedp-tabs{display:inline-flex;background:#fff;border:1px solid var(--f-line);border-radius:12px;padding:4px;gap:3px;box-shadow:var(--f-shadow);width:fit-content;max-width:100%;overflow-x:auto}
        .feedp-tabs button{display:inline-flex;align-items:center;gap:6px;border:none;border-radius:9px;padding:8px 14px;font-size:12.5px;font-weight:600;font-family:inherit;cursor:pointer;background:transparent;color:var(--f-slate);transition:all .2s;white-space:nowrap}
        .feedp-tabs button.on{background:var(--f-navy);color:#fff}

        .feedp-posts{display:grid;gap:14px}
        .feedp :global(.fp-name svg){color:var(--f-teal)}
        .feedp :global(.fp-body h3){font-family:var(--f-display);font-weight:600;font-size:14.5px;color:var(--f-navy);line-height:1.35}
        .feedp :global(.fp-body p){font-size:12.5px;color:var(--f-slate);margin-top:4px;line-height:1.55}
        .feedp :global(.fp-photo img){width:100%;height:240px;object-fit:cover;display:block;background:#eceae2}
        .fp-spacer{flex:1}

        .feedp-skeletons{display:grid;gap:14px}
        .feedp-skeleton{height:130px;border-radius:16px;background:linear-gradient(100deg,#EFECE3 30%,#F7F4EC 50%,#EFECE3 70%);background-size:200% 100%;animation:fsk 1.4s linear infinite}
        @keyframes fsk{from{background-position:200% 0}to{background-position:-200% 0}}

        @media(max-width:1120px){
          .feedp-layout{grid-template-columns:minmax(0,1fr) 260px}
          .feedp-left{display:none}
        }
        @media(max-width:800px){
          .feedp-layout{grid-template-columns:1fr}
          .feedp-right{display:none}
          .feedp-left,.feedp-right{position:static}
        }
        @media(prefers-reduced-motion:reduce){.feedp-skeleton{animation:none}}
      `}</style>
    </main>
  )
}
